import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../../shared/components';
import { ConversationShortcutMenu } from '../../../shared/components/ConversationShortcutMenu';
import type { AiCommandCatalogItem } from '../../ai-commands/types/Command';
import { ComposerEmojiPicker } from './ComposerEmojiPicker';
import { ParticipantAvatar } from '../../../shared/components/ParticipantAvatar';
import { AI_ASSISTANT_AVATAR_URL } from '../../../shared/constants/aiAssistantAvatar';
import { getMessageSourceContent } from '../../../shared/components/MessageSourceContent';
import { ATTACHMENT_ACCEPT, PendingAttachment, formatBytes } from '../../../shared/utils/conversationAttachments';
import { formatDateTime, formatDateTimeInputValue, formatLongDateLabel, formatTime, parseDateTimeInputValue } from '../../../shared/utils/localization';
import { useToast } from '../../notifications';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes, PermissionScopes } from '../../auth/permissions';
import { getCurrentEmployee } from '../../admin/services/employeeService';
import { taskService } from '../../tasks/services/taskService';
import { TaskItem } from '../../tasks/types/Task';
import ConversationChannelBadge from './ConversationChannelBadge';
import { AiVoiceCallControl } from './AiVoiceCallControl';
import { ConversationRequestPaymentButton } from '../../payments/components/ConversationRequestPaymentButton';
import { InstagramContentPreviewCard } from './InstagramContentPreviewCard';
import { getParticipantChannelSummary, getParticipantConversationIcons, getParticipantConversationIconTitles, getConversationHeaderChannelIcons, getConversationChannelIconTitles, getChannelBadgeInlineLabel, getParticipantChannelIcon, resolveExternalConversationAvatarIcon } from '../conversationChannelIcons';
import { addConversationParticipants, ConversationAiOperationItem, ConversationAiRunUsageSummary, getConversationAiOperations, getParticipantOptions, linkExistingTaskToConversation, listConversationTasks, ScheduledMessage } from '../services/conversationService';
import ConversationCreateTaskModal from './ConversationCreateTaskModal';
import WhatsAppAppGroupPanel from './WhatsAppAppGroupPanel';
import ConversationAiSettingsPanel from './ConversationAiSettingsPanel';
import { ConversationAssigneePicker } from './ConversationAssigneePicker';
import { Conversation, ConversationInteractiveButtons, ConversationInteractiveList, ConversationParticipantOption, ConversationParticipantRef, Message, ParticipantType } from '../types/Conversation';
import type { ChannelAccountChannel } from '../../connectors/types/channelAccount';
import { channelIconKindToAccountChannel, useChannelReadiness } from '../hooks/useChannelReadiness';
import { getConversationKind, isAiAssistantDirectConversation, isExternalConversation, isWhatsAppGroupConversation } from '../utils/conversationClassification';
import { getQuotedMessageSnippet, idsEqual, isExternalReplyAuditMessage, isOutgoingConversationMessage, truncateMessageSnippet } from '../utils/messageUtils';
import { isOptimisticMessageId } from '../utils/optimisticMessages';
import { buildChannelDeliveryTooltip } from '../utils/whatsappComposerUtils';
import { getInstagramCommentSurfaceLabelKey, getInstagramComposerPlaceholderKey, getInstagramReplyModeLabelKey, instagramCommentReplyBlocksAttachments, instagramComposerShowsReplyTarget, isInstagramCommentMessage, isInstagramCommentReplyTarget, findInstagramReplyMessageForComment, resolveConversationHasInstagramComments, filterMessagesForCommentVisibility, type InstagramComposerContext, type InstagramReplyMode } from '../utils/instagramComposerUtils';
import { getMessengerComposerPlaceholderKey, getMessengerReplyModeLabelKey, messengerCommentReplyBlocksAttachments, messengerComposerShowsReplyTarget, isMessengerCommentMessage, isMessengerCommentReplyTarget, findMessengerReplyMessageForComment, resolveConversationHasMessengerComments, type MessengerComposerContext, type MessengerReplyMode } from '../utils/messengerComposerUtils';
import { InstagramComposerReplyTarget } from './InstagramComposerReplyTarget';
import { MessengerComposerReplyTarget } from './MessengerComposerReplyTarget';
import ScheduledMessagesPanel from './ScheduledMessagesPanel';
// ...existing code...
import detailsStyles from './ConversationDetails.module.css';
import pageStyles from './ConversationsPage.module.css';
import type { AiThinkingStepEvent } from '../hooks/useConversationRealtimeSubscription';
import { useAnchoredPopover } from '../hooks/useAnchoredPopover';
import { isAiThinkingTerminalStep } from '../utils/aiThinkingSteps';
import { resolveConversationInteractiveButtonMaxCount } from '../utils/interactiveButtonLimits';
import { isInteractiveButtonAddDisabled } from '../../../shared/utils/interactiveButtonLimits';

/** Brand names, so they read the same in every locale. */
const CHANNEL_DISPLAY_NAMES: Record<ChannelAccountChannel, string> = {
  WhatsApp: 'WhatsApp',
  Instagram: 'Instagram',
  Telegram: 'Telegram',
  Messenger: 'Messenger',
  TikTok: 'TikTok',
  Webchat: 'Web chat',
  Voice: 'Calls',
  Sms: 'SMS',
};

interface ConversationFileItem {
  id: string;
  url: string;
  name: string;
  typeKey: string;
  typeLabel: string;
  timestamp: string;
  senderName: string;
}

interface GroupedConversationFiles {
  key: string;
  title: string;
  items: ConversationFileItem[];
}

const formatAiActivityValue = (value?: string) => {
  if (!value) {
    return '';
  }

  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const formatAiActivityLabel = (value: string, fallback: string) => {
  const normalized = value.trim();
  if (!normalized) {
    return fallback;
  }

  return normalized
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());
};

const aiActivityPluginLabels: Record<string, string> = {
  'conversation-memory': 'Conversation memory',
  model: 'Model',
  task: 'Task',
  taskread: 'Task read',
  resource: 'Resource',
  time: 'Time',
  conversationsender: 'Conversation sender',
  interactivemessage: 'Interactive message',
  interactivebuttonmessage: 'Interactive button message',
  consultation: 'Consultation',
  consultationread: 'Consultation read',
  dynamicknowledge: 'Dynamic knowledge',
};

const aiActivityFunctionLabels: Record<string, string> = {
  'conversation-memory:refresh': 'Refresh memory',
  'conversation-memory:clear': 'Clear memory',
  'conversation-memory:prepare': 'Prepare memory',
  'model:completion': 'Completion',
  'taskread:gettasks': 'Get tasks',
  'task:create': 'Create task',
  'task:update': 'Update task',
  'consultationread:getconsultations': 'Get consultations',
  'consultation:create': 'Create consultation',
};

const formatAiActivityPluginLabel = (value: string) => {
  const normalized = value.trim().toLowerCase();
  if (normalized in aiActivityPluginLabels) {
    return aiActivityPluginLabels[normalized];
  }

  return formatAiActivityLabel(value, 'Unknown plugin');
};

const formatAiActivityFunctionLabel = (pluginName: string, functionName: string) => {
  const normalizedPlugin = pluginName.trim().toLowerCase();
  const normalizedFunction = functionName.trim().toLowerCase();
  const compositeKey = `${normalizedPlugin}:${normalizedFunction}`;

  if (compositeKey in aiActivityFunctionLabels) {
    return aiActivityFunctionLabels[compositeKey];
  }

  return formatAiActivityLabel(functionName, 'Unknown function');
};

const formatAiTokenCount = (value?: number) => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return null;
  }

  return new Intl.NumberFormat().format(value);
};

const formatAiRunLabel = (runId: string) => runId.length > 8 ? `${runId.slice(0, 8)}…` : runId;

const AI_THINKING_AUTO_HIDE_MS = 2500;

const getConversationTaskLink = (task: TaskItem, conversationId: string) => {
  return task.conversationLinks.find(link => link.conversationId === conversationId);
};

const buildMessageTaskTitle = (message: Message, senderName: string, fallback: string) => {
  const snippet = truncateMessageSnippet(message.content || fallback, 48).trim();
  if (!snippet || snippet === fallback) {
    return `Follow up: ${senderName}`;
  }

  return `${senderName}: ${snippet}`;
};

const renderPendingStatusIcon = () => (
  <svg className={detailsStyles.statusIconSvg} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
    <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.4" strokeDasharray="2.5 2.5" />
  </svg>
);

const renderMessageStatusIcon = (status: 'sent' | 'delivered' | 'failed') => {
  if (status === 'sent') {
    return (
      <svg className={detailsStyles.statusIconSvg} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
        <circle cx="8" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    );
  }

  if (status === 'delivered') {
    return (
      <svg className={detailsStyles.statusIconSvg} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
        <path d="M2.5 8.5 5 11l3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7 8.5 9.5 11 13 7.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return (
    <svg className={detailsStyles.statusIconSvg} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <path d="M4.5 4.5 11.5 11.5M11.5 4.5 4.5 11.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
};

const renderDocumentIcon = (className: string) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="M7 3.5h7l4 4V20a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 20V5A1.5 1.5 0 0 1 7.5 3.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M14 3.5V8h4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M9 12h6M9 15h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const renderPanelCloseIcon = (className: string) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const renderLinkedTasksIcon = (className: string) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="M8 6h13M8 12h13M8 18h13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M4.5 7.5 6 9l2.5-3M4.5 13.5 6 15l2.5-3M4.5 19.5 6 21l2.5-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const renderFilesPanelIcon = (className: string) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="M7 4.5h7l3 3V19a1.5 1.5 0 0 1-1.5 1.5h-8A1.5 1.5 0 0 1 6 19V6A1.5 1.5 0 0 1 7.5 4.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 4.5V8h3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const renderAttachmentTypeIcon = (kind: PendingAttachment['kind']) => {
  if (kind === 'image') {
    return (
      <svg className={detailsStyles.attachmentTypeIconSvg} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="9" cy="10" r="1.7" fill="currentColor" />
        <path d="m6.5 16 3.5-3 2.6 2.2 2.8-2.5 2.1 3.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (kind === 'audio') {
    return (
      <svg className={detailsStyles.attachmentTypeIconSvg} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <path d="M6 10v4h3l4 3V7l-4 3H6Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16.5 9.5a4.5 4.5 0 0 1 0 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }

  if (kind === 'video') {
    return (
      <svg className={detailsStyles.attachmentTypeIconSvg} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <rect x="3.5" y="6" width="12" height="12" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="m15.5 10.2 5-2v7.6l-5-2z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      </svg>
    );
  }

  return renderDocumentIcon(detailsStyles.attachmentTypeIconSvg);
};

const renderSoundIcon = (enabled: boolean) => {
  if (enabled) {
    return (
      <svg className={detailsStyles.soundToggleIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <path d="M6 10v4h3l4 3V7l-4 3H6Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16.5 9.5a4.5 4.5 0 0 1 0 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg className={detailsStyles.soundToggleIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path d="M6 10v4h3l4 3V7l-4 3H6Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="m17 10 4 4M21 10l-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
};

interface ConversationsDetailsPaneProps {
  isListExpanded: boolean;
  isDetailsMaximized: boolean;
  onDetailsMaximizedChange: (maximized: boolean) => void;
  isMobileLayout: boolean;
  onBackToList: () => void;
  expandScheduledMessagesSignal: number;
  selectedId: string | null;
  detailsLoading: boolean;
  detailsError: string | null;
  selectedConv: Conversation | null;
  showCommentThreads: boolean;
  signing: boolean;
  toggleAiAssistantBusy: boolean;
  onSign: () => void;
  getConversationDisplayName: (conversation: Conversation) => string;
  notificationPermission: NotificationPermission;
  onOpenConversationNotes: (conversationId: string) => void;
  loadingOlderMessages: boolean;
  onLoadOlderMessages: () => Promise<boolean>;
  onLoadOlderMessagesForJump: (messageId: string) => Promise<boolean>;
  getParticipantDisplayName: (participant: ConversationParticipantRef) => string;
  showFilesPanel: boolean;
  setShowFilesPanel: React.Dispatch<React.SetStateAction<boolean>>;
  conversationFiles: ConversationFileItem[];
  onToggleAiAssistant: () => void;
  aiHistoryStartSaving: boolean;
  onUpdateAiHistoryStartAtUtc: (value: string | null) => void | Promise<void>;
  isSoundEnabled: boolean;
  onToggleSound: () => void;
  filesSearchTerm: string;
  setFilesSearchTerm: (value: string) => void;
  groupedConversationFiles: GroupedConversationFiles[];
  filesPanelRef: React.RefObject<HTMLDivElement | null>;
  messagesPaneRef: React.RefObject<HTMLDivElement | null>;
  meSender: ConversationParticipantRef | undefined;
  getMessageTypeLabel: (messageType?: string) => string;
  showReactionPicker: string | null;
  pendingReactionKeys: Set<string>;
  setShowReactionPicker: (messageId: string | null) => void;
  onReaction: (messageId: string, emoji: string) => void;
  onModerateInstagramComment: (messageId: string, action: 'hide' | 'unhide' | 'delete') => void | Promise<void>;
  onModerateMessengerComment: (messageId: string, action: 'hide' | 'unhide' | 'delete') => void | Promise<void>;
  onDeleteMessage?: (messageId: string) => void | Promise<void>;
  translatingMessageId: string | null;
  translatedMessagesById: Record<string, string>;
  translatedMessageVisibilityById: Record<string, boolean>;
  onTranslateMessage: (message: Message) => void | Promise<void>;
  onToggleTranslatedMessageVisibility: (messageId: string) => void;
  renderMessageContent: (message: Message) => React.ReactNode;
  mentionDetailsPopup: React.ReactNode;
  imageViewerPopup: React.ReactNode;
  unreadCount: number;
  scrollToBottom: () => void;
  aiDraft: string;
  aiDraftError: string | null;
  aiSummary: string;
  aiSummaryError: string | null;
  aiMemory: string;
  aiMemoryError: string | null;
  aiMemoryLoading: boolean;
  refreshingAiMemory: boolean;
  clearingAiMemory: boolean;
  copyingAiMemory: boolean;
  aiThinkingSteps: AiThinkingStepEvent[];
  savingAiSummaryAsNote: boolean;
  copyingAiSummary: boolean;
  onUseAiDraft: () => void;
  onUseAiSummary: () => void;
  onDismissAiDraft: () => void;
  onDismissAiSummary: () => void;
  onSaveAiSummaryAsNote: () => void;
  onCopyAiSummary: () => void;
  onCopyAiMemory: () => void;
  onRefreshAiMemory: () => void;
  onClearAiMemory: () => void;
  quotedMessage: Message | null;
  onQuoteMessage: (message: Message) => void;
  onSelectInstagramCommentTarget: (message: Message) => void;
  onClearInstagramCommentTarget: () => void;
  onSelectMessengerCommentTarget: (message: Message) => void;
  onClearMessengerCommentTarget: () => void;
  onClearQuotedMessage: () => void;
  dragActive: boolean;
  onComposerDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onComposerDragLeave: (event: React.DragEvent<HTMLDivElement>) => void;
  onComposerDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  composerFileInputRef: React.RefObject<HTMLInputElement | null>;
  onPickAttachments: (fileList?: FileList | File[] | null) => void;
  isUploaderExpanded: boolean;
  setIsUploaderExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  isRecordingAudio: boolean;
  onAudioRecordToggle: () => Promise<void>;
  isShortcutMenuOpen: boolean;
  setIsShortcutMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onShortcutSelectCommand: (command: string) => void;
  insertComposerEmoji: (emoji: string) => void;
  onGenerateAiDraft: () => void;
  aiDraftLoading: boolean;
  onGenerateAiSummary: () => void;
  aiSummaryLoading: boolean;
  scheduledAt: Date | null;
  setScheduledAt: React.Dispatch<React.SetStateAction<Date | null>>;
  scheduling: boolean;
  scheduleError: string | null;
  scheduledMessages: ScheduledMessage[];
  onSchedule: () => Promise<void>;
  onScheduleAt: (scheduledAt: Date) => Promise<void>;
  onCancelScheduledMessage: (scheduledMessageId: string) => Promise<void>;
  onUpdateScheduledMessageTime: (scheduledMessageId: string, scheduledAt: Date, content?: string) => Promise<void>;
  attachments: PendingAttachment[];
  composer: string;
  mentionSuggestions: ConversationParticipantOption[];
  mentionMenuOpen: boolean;
  mentionActiveIndex: number;
  commandSuggestions?: AiCommandCatalogItem[];
  commandMenuOpen?: boolean;
  commandActiveIndex?: number;
  interactiveComposerMode: 'list' | 'buttons';
  setInteractiveComposerMode: React.Dispatch<React.SetStateAction<'list' | 'buttons'>>;
  interactiveListDraft: ConversationInteractiveList;
  setInteractiveListDraft: React.Dispatch<React.SetStateAction<ConversationInteractiveList>>;
  interactiveButtonsDraft: ConversationInteractiveButtons;
  setInteractiveButtonsDraft: React.Dispatch<React.SetStateAction<ConversationInteractiveButtons>>;
  isInteractiveComposerOpen: boolean;
  setIsInteractiveComposerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  hasInteractiveListDraft: boolean;
  hasInteractiveButtonsDraft: boolean;
  hasInteractiveDraft: boolean;
  removeAttachment: (attachmentId: string) => void;
  sendError: string | null;
  sendingMessage: boolean;
  sendingProgressLabel: string | null;
  composerInputRef: React.RefObject<HTMLTextAreaElement | null>;
  onComposerChange: (value: string, caretPosition: number) => void;
  onComposerSelectionChange: (caretPosition: number) => void;
  onComposerFocus: () => void;
  onComposerKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => boolean;
  showWhatsAppLinkPreviewToggle: boolean;
  whatsAppLinkPreviewEnabled: boolean;
  setWhatsAppLinkPreviewEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  instagramComposerContext: InstagramComposerContext;
  instagramReplyMode: InstagramReplyMode | null;
  setInstagramReplyMode: React.Dispatch<React.SetStateAction<InstagramReplyMode | null>>;
  instagramCommentTargetMessageId: string | null;
  setInstagramCommentTargetMessageId: React.Dispatch<React.SetStateAction<string | null>>;
  messengerComposerContext: MessengerComposerContext;
  messengerReplyMode: MessengerReplyMode | null;
  setMessengerReplyMode: React.Dispatch<React.SetStateAction<MessengerReplyMode | null>>;
  messengerCommentTargetMessageId: string | null;
  setMessengerCommentTargetMessageId: React.Dispatch<React.SetStateAction<string | null>>;
  onSelectMention: (option: ConversationParticipantOption) => void;
  onSelectCommand?: (command: AiCommandCatalogItem) => void;
  onSend: () => Promise<void>;
  employeeOptions: Array<{ id: string; name: string }>;
  onAssignConversation: (conversationId: string, employeeId: string) => void | Promise<void>;
  onUnassignConversation: (conversationId: string) => void | Promise<void>;
  maskChannelValues?: boolean;
}

export const ConversationsDetailsPane: React.FC<ConversationsDetailsPaneProps> = ({
  isListExpanded,
  isDetailsMaximized,
  onDetailsMaximizedChange,
  isMobileLayout,
  onBackToList,
  expandScheduledMessagesSignal,
  selectedId,
  detailsLoading,
  detailsError,
  selectedConv,
  showCommentThreads,
  signing,
  toggleAiAssistantBusy,
  onSign,
  getConversationDisplayName,
  notificationPermission,
  onOpenConversationNotes,
  loadingOlderMessages,
  onLoadOlderMessages,
  onLoadOlderMessagesForJump,
  getParticipantDisplayName,
  showFilesPanel,
  setShowFilesPanel,
  conversationFiles,
  onToggleAiAssistant,
  aiHistoryStartSaving,
  onUpdateAiHistoryStartAtUtc,
  isSoundEnabled,
  onToggleSound,
  filesSearchTerm,
  setFilesSearchTerm,
  groupedConversationFiles,
  filesPanelRef,
  messagesPaneRef,
  meSender,
  getMessageTypeLabel,
  showReactionPicker,
  pendingReactionKeys,
  setShowReactionPicker,
  onReaction,
  onModerateInstagramComment,
  onModerateMessengerComment,
  onDeleteMessage,
  translatingMessageId,
  translatedMessagesById,
  translatedMessageVisibilityById,
  onTranslateMessage,
  onToggleTranslatedMessageVisibility,
  renderMessageContent,
  mentionDetailsPopup,
  imageViewerPopup,
  unreadCount,
  scrollToBottom,
  aiDraft,
  aiDraftError,
  aiSummary,
  aiSummaryError,
  aiMemory,
  aiMemoryError,
  aiMemoryLoading,
  refreshingAiMemory,
  clearingAiMemory,
  copyingAiMemory,
  aiThinkingSteps,
  savingAiSummaryAsNote,
  copyingAiSummary,
  onUseAiDraft,
  onUseAiSummary,
  onDismissAiDraft,
  onDismissAiSummary,
  onSaveAiSummaryAsNote,
  onCopyAiSummary,
  onCopyAiMemory,
  onRefreshAiMemory,
  onClearAiMemory,
  quotedMessage,
  onQuoteMessage,
  onSelectInstagramCommentTarget,
  onClearInstagramCommentTarget,
  onSelectMessengerCommentTarget,
  onClearMessengerCommentTarget,
  onClearQuotedMessage,
  dragActive,
  onComposerDragOver,
  onComposerDragLeave,
  onComposerDrop,
  composerFileInputRef,
  onPickAttachments,
  isRecordingAudio,
  onAudioRecordToggle,
  isShortcutMenuOpen,
  setIsShortcutMenuOpen,
  onShortcutSelectCommand,
  insertComposerEmoji,
  onGenerateAiDraft,
  aiDraftLoading,
  onGenerateAiSummary,
  aiSummaryLoading,
  scheduledAt,
  setScheduledAt,
  scheduling,
  scheduleError,
  scheduledMessages,
  onSchedule,
  onScheduleAt,
  onCancelScheduledMessage,
  onUpdateScheduledMessageTime,
  attachments,
  composer,
  mentionSuggestions,
  mentionMenuOpen,
  mentionActiveIndex,
  commandSuggestions = [],
  commandMenuOpen = false,
  commandActiveIndex = 0,
  interactiveComposerMode,
  setInteractiveComposerMode,
  interactiveListDraft,
  setInteractiveListDraft,
  interactiveButtonsDraft,
  setInteractiveButtonsDraft,
  isInteractiveComposerOpen,
  setIsInteractiveComposerOpen,
  hasInteractiveListDraft,
  hasInteractiveButtonsDraft,
  hasInteractiveDraft,
  removeAttachment,
  sendError,
  sendingMessage,
  sendingProgressLabel,
  composerInputRef,
  onComposerChange,
  onComposerSelectionChange,
  onComposerFocus,
  onComposerKeyDown,
  showWhatsAppLinkPreviewToggle,
  whatsAppLinkPreviewEnabled,
  setWhatsAppLinkPreviewEnabled,
  instagramComposerContext,
  instagramReplyMode,
  setInstagramReplyMode,
  instagramCommentTargetMessageId,
  setInstagramCommentTargetMessageId,
  messengerComposerContext,
  messengerReplyMode,
  setMessengerReplyMode,
  messengerCommentTargetMessageId,
  setMessengerCommentTargetMessageId,
  onSelectMention,
  onSelectCommand,
  onSend,
  employeeOptions,
  onAssignConversation,
  onUnassignConversation,
  maskChannelValues = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission, auth } = useAuth();
  const { showToast } = useToast();
  const canManageTasks = hasPermission([PermissionCodes.tasksManage]) || hasPermission(PermissionScopes.tasksRead);
  const [linkedTasks, setLinkedTasks] = React.useState<TaskItem[]>([]);
  const [linkedTasksLoading, setLinkedTasksLoading] = React.useState(false);
  const [linkedTasksError, setLinkedTasksError] = React.useState<string | null>(null);
  const [isLinkedTasksExpanded, setIsLinkedTasksExpanded] = React.useState(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = React.useState(false);
  const [isTaskPickerOpen, setIsTaskPickerOpen] = React.useState(false);
  const [taskSearchTerm, setTaskSearchTerm] = React.useState('');
  const [taskSearchResults, setTaskSearchResults] = React.useState<TaskItem[]>([]);
  const [taskSearchLoading, setTaskSearchLoading] = React.useState(false);
  const [taskSearchError, setTaskSearchError] = React.useState<string | null>(null);
  const [linkTaskAsPrimary, setLinkTaskAsPrimary] = React.useState(false);
  const [linkingTaskId, setLinkingTaskId] = React.useState<string | null>(null);
  const [promotingTaskId, setPromotingTaskId] = React.useState<string | null>(null);
  const [unlinkingTaskId, setUnlinkingTaskId] = React.useState<string | null>(null);
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = React.useState(false);
  const [createTaskMessage, setCreateTaskMessage] = React.useState<Message | null>(null);
  const [participantOptions, setParticipantOptions] = React.useState<ConversationParticipantOption[]>([]);
  const [participantOptionsLoading, setParticipantOptionsLoading] = React.useState(false);
  const [participantOptionsError, setParticipantOptionsError] = React.useState<string | null>(null);
  const [currentEmployeeAvatarUrl, setCurrentEmployeeAvatarUrl] = React.useState<string | undefined>(undefined);
  const [addParticipantsError, setAddParticipantsError] = React.useState<string | null>(null);
  const {
    rootRef: participantsMenuRef,
    triggerRef: participantsToggleRef,
    popoverRef: participantsPopoverRef,
    isOpen: isParticipantsMenuOpen,
    popoverStyle: participantsPopoverStyle,
    toggle: toggleParticipantsMenu,
    close: closeParticipantsMenu,
  } = useAnchoredPopover({ popoverWidth: 340, minSpaceBelow: 260 });
  const [isAddParticipantsOpen, setIsAddParticipantsOpen] = React.useState(false);
  const [participantSearchTerm, setParticipantSearchTerm] = React.useState('');
  const [selectedParticipantKeys, setSelectedParticipantKeys] = React.useState<Set<string>>(new Set());
  const [addingParticipants, setAddingParticipants] = React.useState(false);
  const [isAiThinkingPanelOpen, setIsAiThinkingPanelOpen] = React.useState(false);
  const [isAiThinkingCompletionVisible, setIsAiThinkingCompletionVisible] = React.useState(false);
  const [isAiHistorySettingsOpen, setIsAiHistorySettingsOpen] = React.useState(false);
  const [isAiActivityPanelOpen, setIsAiActivityPanelOpen] = React.useState(false);
  const [isAiSettingsPanelOpen, setIsAiSettingsPanelOpen] = React.useState(false);
  const [isAiMemorySectionExpanded, setIsAiMemorySectionExpanded] = React.useState(false);
  const [aiActivityItems, setAiActivityItems] = React.useState<ConversationAiOperationItem[]>([]);
  const [aiActivityRunSummaries, setAiActivityRunSummaries] = React.useState<ConversationAiRunUsageSummary[]>([]);
  const [aiActivityLoading, setAiActivityLoading] = React.useState(false);
  const [aiActivityLoadingMore, setAiActivityLoadingMore] = React.useState(false);
  const [aiActivityError, setAiActivityError] = React.useState<string | null>(null);
  const [aiActivityHasMore, setAiActivityHasMore] = React.useState(false);
  const [aiActivityNextBefore, setAiActivityNextBefore] = React.useState<string | undefined>(undefined);
  const [aiActivityExpandedIds, setAiActivityExpandedIds] = React.useState<Set<string>>(new Set());
  const [aiActivityStatusFilter, setAiActivityStatusFilter] = React.useState('all');
  const [aiActivityPluginFilter, setAiActivityPluginFilter] = React.useState('all');
  const [aiActivityTriggeredByFilter, setAiActivityTriggeredByFilter] = React.useState('all');
  const [aiActivityRunIdFilter, setAiActivityRunIdFilter] = React.useState<string | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = React.useState<string | null>(null);
  const [collapsedInteractiveSections, setCollapsedInteractiveSections] = React.useState<Set<number>>(new Set());
  const [isScheduleMenuOpen, setIsScheduleMenuOpen] = React.useState(false);
  const [isScheduledPanelExpanded, setIsScheduledPanelExpanded] = React.useState(false);
  const detailsPaneRef = useRef<HTMLDivElement>(null);

  const syncFullscreenState = useCallback(() => {
    const element = detailsPaneRef.current;
    const isFullscreen = Boolean(element && document.fullscreenElement === element);
    onDetailsMaximizedChange(isFullscreen);
  }, [onDetailsMaximizedChange]);

  const onToggleDetailsMaximized = useCallback(async () => {
    const element = detailsPaneRef.current;
    if (!element) {
      return;
    }

    try {
      if (document.fullscreenElement === element) {
        await document.exitFullscreen();
        return;
      }

      if (isDetailsMaximized) {
        onDetailsMaximizedChange(false);
        return;
      }

      onDetailsMaximizedChange(true);

      if (typeof element.requestFullscreen === 'function') {
        await element.requestFullscreen();
      }
    } catch {
      if (document.fullscreenElement !== element) {
        onDetailsMaximizedChange(false);
      }
    }
  }, [isDetailsMaximized, onDetailsMaximizedChange]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      syncFullscreenState();
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [syncFullscreenState]);

  useEffect(() => {
    const element = detailsPaneRef.current;
    if (!selectedId && element && document.fullscreenElement === element) {
      void document.exitFullscreen();
    }
  }, [selectedId]);

  useEffect(() => {
    setIsEmojiPickerOpen(false);
  }, [selectedId]);

  const [showCustomScheduleInput, setShowCustomScheduleInput] = React.useState(false);
  const [customScheduleValue, setCustomScheduleValue] = React.useState('');
  const previousThinkingStepCountRef = React.useRef(0);
  const aiThinkingAutoHideTimeoutRef = React.useRef<number | null>(null);
  const previousAiBusyRef = React.useRef(false);
  const highlightTimeoutRef = React.useRef<number | null>(null);
  const scheduleMenuRef = React.useRef<HTMLDivElement | null>(null);
  const participantSearchInputRef = React.useRef<HTMLInputElement | null>(null);
  const aiHistorySettingsRef = React.useRef<HTMLDivElement | null>(null);
  const participantOptionByKey = React.useMemo(
    () => new Map(participantOptions.map(option => [`${option.type}:${option.id}`, option])),
    [participantOptions],
  );
  const isSelectedConversationExternal = Boolean(selectedConv && isExternalConversation(selectedConv));
  const isInstagramCommentThread = Boolean(selectedConv && resolveConversationHasInstagramComments(selectedConv));
  const isMessengerCommentThread = Boolean(selectedConv && resolveConversationHasMessengerComments(selectedConv));
  const displayMessages = React.useMemo(
    () => (selectedConv
      ? filterMessagesForCommentVisibility(selectedConv.messages, showCommentThreads)
      : []),
    [selectedConv, showCommentThreads],
  );
  const showInstagramReplyTarget = instagramComposerShowsReplyTarget(
    instagramCommentTargetMessageId,
    instagramReplyMode,
    instagramComposerContext,
  );
  const showMessengerReplyTarget = messengerComposerShowsReplyTarget(
    messengerCommentTargetMessageId,
    messengerReplyMode,
    messengerComposerContext,
  );
  const commentRichContentBlocked = (showInstagramReplyTarget && instagramCommentReplyBlocksAttachments(instagramReplyMode))
    || (showMessengerReplyTarget && messengerCommentReplyBlocksAttachments(messengerReplyMode));
  const activeInstagramCommentTargetId = instagramCommentTargetMessageId;
  const activeMessengerCommentTargetId = messengerCommentTargetMessageId;
  const showGenericQuote = Boolean(
    quotedMessage
    && !(showInstagramReplyTarget && quotedMessage && isInstagramCommentMessage(quotedMessage))
    && !(showMessengerReplyTarget && quotedMessage && isMessengerCommentMessage(quotedMessage)),
  );
  const composerPlaceholderKey = showMessengerReplyTarget
    ? getMessengerComposerPlaceholderKey(messengerReplyMode, {
      isInteractiveComposerOpen,
      attachmentCount: attachments.length,
    })
    : getInstagramComposerPlaceholderKey(instagramReplyMode, {
      isInteractiveComposerOpen,
      attachmentCount: attachments.length,
    });
  const canViewAiHistorySettings = hasPermission([PermissionCodes.adminAccess]);
  const canManageConversations = hasPermission([PermissionCodes.conversationsManage]);
  const canReadTasks = hasPermission(PermissionScopes.tasksRead) || hasPermission([PermissionCodes.tasksManage]);
  const canAssignConversations = hasPermission([
    PermissionCodes.conversationsAssign,
    PermissionCodes.conversationsManage,
  ]);
  const showAssigneeSelector = Boolean(selectedId && canAssignConversations && employeeOptions.length > 0);
  const isAssigneeUpdating = selectedConv?.assignedToEmployeeId === '__pending__';
  const isAiThinkingInProgress = aiDraftLoading || aiSummaryLoading;
  const clearAiThinkingAutoHide = React.useCallback(() => {
    if (aiThinkingAutoHideTimeoutRef.current !== null) {
      window.clearTimeout(aiThinkingAutoHideTimeoutRef.current);
      aiThinkingAutoHideTimeoutRef.current = null;
    }
  }, []);

  const hideAiThinkingStatus = React.useCallback(() => {
    clearAiThinkingAutoHide();
    setIsAiThinkingPanelOpen(false);
    setIsAiThinkingCompletionVisible(false);
  }, [clearAiThinkingAutoHide]);

  const scheduleAiThinkingAutoHide = React.useCallback(() => {
    clearAiThinkingAutoHide();
    setIsAiThinkingCompletionVisible(true);
    setIsAiThinkingPanelOpen(true);

    aiThinkingAutoHideTimeoutRef.current = window.setTimeout(() => {
      hideAiThinkingStatus();
    }, AI_THINKING_AUTO_HIDE_MS);
  }, [clearAiThinkingAutoHide, hideAiThinkingStatus]);

  const showAiThinkingComposerStatus = Boolean(selectedConv)
    && !isAiActivityPanelOpen
    && (isAiThinkingInProgress || isAiThinkingPanelOpen || isAiThinkingCompletionVisible);
  const latestThinkingStep = aiThinkingSteps.length > 0
    ? aiThinkingSteps[aiThinkingSteps.length - 1]
    : null;
  const latestBroadcastThinkingMessage = latestThinkingStep
    ? (latestThinkingStep.detail?.trim() || latestThinkingStep.step.trim())
    : null;
  const isAiBusy = aiDraftLoading || aiSummaryLoading;
  const isWhatsAppGroup = Boolean(selectedConv && isWhatsAppGroupConversation(selectedConv));
  const isWhatsAppAppLine = selectedConv?.whatsAppConnectionKind === 'app' || isWhatsAppGroup;
  const canSend = (isInteractiveComposerOpen ? hasInteractiveDraft : (!!composer.trim() || attachments.length > 0)) && !!meSender && !sendingMessage;
  const scheduleDisabled = !canSend || scheduling || isInteractiveComposerOpen || sendingMessage;
  const interactiveSectionCount = interactiveListDraft.sections.length;
  const interactiveOptionCount = interactiveListDraft.sections.reduce((total, section) => total + section.rows.length, 0);
  const interactiveButtonCount = interactiveButtonsDraft.buttons.length;
  const interactiveButtonMaxCount = React.useMemo(
    () => resolveConversationInteractiveButtonMaxCount(selectedConv),
    [selectedConv],
  );
  const isInteractiveButtonAddAtLimit = isInteractiveButtonAddDisabled(
    interactiveButtonCount,
    interactiveButtonMaxCount,
  );
  const aiActivityPluginOptions = React.useMemo(
    () => Array.from(new Set(aiActivityItems.map(item => item.pluginName).filter(Boolean))).sort((left, right) => left.localeCompare(right)),
    [aiActivityItems],
  );
  const visibleAiRunSummaries = React.useMemo(
    () => aiActivityRunIdFilter
      ? aiActivityRunSummaries.slice(0, 1)
      : aiActivityRunSummaries.slice(0, 3),
    [aiActivityRunIdFilter, aiActivityRunSummaries],
  );

  const loadLinkedTasks = React.useCallback(async (conversationId: string) => {
    setLinkedTasksLoading(true);
    setLinkedTasksError(null);
    try {
      const items = await listConversationTasks(conversationId);
      setLinkedTasks(items);
    } catch {
      setLinkedTasksError(t('failedToLoadLinkedTasks', { ns: 'conversations' }));
    } finally {
      setLinkedTasksLoading(false);
    }
  }, [t]);

  const loadAiActivity = React.useCallback(async (conversationId: string, append: boolean) => {
    if (append) {
      setAiActivityLoadingMore(true);
    } else {
      setAiActivityLoading(true);
      setAiActivityError(null);
    }

    try {
      const response = await getConversationAiOperations(conversationId, {
        limit: 20,
        before: append ? aiActivityNextBefore : undefined,
        status: aiActivityStatusFilter !== 'all' ? aiActivityStatusFilter : undefined,
        plugin: aiActivityPluginFilter !== 'all' ? aiActivityPluginFilter : undefined,
        triggeredBy: aiActivityTriggeredByFilter !== 'all' ? aiActivityTriggeredByFilter : undefined,
        runId: aiActivityRunIdFilter || undefined,
      });

      if (!response) {
        setAiActivityItems([]);
        setAiActivityRunSummaries([]);
        setAiActivityHasMore(false);
        setAiActivityNextBefore(undefined);
        return;
      }

      setAiActivityItems(current => {
        if (!append) {
          return response.items;
        }

        const existingIds = new Set(current.map(item => item.id));
        return [...current, ...response.items.filter(item => !existingIds.has(item.id))];
      });
      setAiActivityHasMore(response.hasMore);
      setAiActivityNextBefore(response.nextBefore);
      setAiActivityRunSummaries(response.runSummaries);
    } catch {
      const message = t('failedToLoadAiActivity', { ns: 'conversations', defaultValue: 'Failed to load AI activity.' });
      setAiActivityError(message);
      if (!append) {
        setAiActivityItems([]);
        setAiActivityRunSummaries([]);
        setAiActivityHasMore(false);
        setAiActivityNextBefore(undefined);
      }
    } finally {
      if (append) {
        setAiActivityLoadingMore(false);
      } else {
        setAiActivityLoading(false);
      }
    }
  }, [aiActivityNextBefore, aiActivityPluginFilter, aiActivityRunIdFilter, aiActivityStatusFilter, aiActivityTriggeredByFilter, t]);

  const copyVisibleAiActivity = React.useCallback(async () => {
    if (aiActivityItems.length === 0 && visibleAiRunSummaries.length === 0) {
      return;
    }

    try {
      const payload = {
        conversationId: selectedConv?.id,
        runFilter: aiActivityRunIdFilter,
        exportedAt: new Date().toISOString(),
        runSummaries: visibleAiRunSummaries,
        items: aiActivityItems,
      };
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      showToast(t('copiedAiActivityExport', { ns: 'conversations', defaultValue: 'AI activity copied.' }), 'success');
    } catch {
      showToast(t('failedToCopyAiActivityExport', { ns: 'conversations', defaultValue: 'Failed to copy AI activity.' }), 'error');
    }
  }, [aiActivityItems, aiActivityRunIdFilter, selectedConv?.id, showToast, t, visibleAiRunSummaries]);

  const copyAiActivityPayload = React.useCallback(async (value: string | undefined, label: string) => {
    if (!value?.trim()) {
      return;
    }

    try {
      await navigator.clipboard.writeText(formatAiActivityValue(value));
      showToast(t('copiedAiActivityPayload', { ns: 'conversations', defaultValue: '{{label}} copied.', label }), 'success');
    } catch {
      showToast(t('failedToCopyAiActivityPayload', { ns: 'conversations', defaultValue: 'Failed to copy {{label}}.', label }), 'error');
    }
  }, [showToast, t]);

  React.useEffect(() => {
    if (!selectedConv?.id || !canManageTasks) {
      setLinkedTasks([]);
      setLinkedTasksLoading(false);
      setLinkedTasksError(null);
      return;
    }

    void loadLinkedTasks(selectedConv.id);
  }, [canManageTasks, loadLinkedTasks, selectedConv?.id]);

  React.useEffect(() => {
    setIsTaskPickerOpen(false);
    setTaskSearchTerm('');
    setTaskSearchResults([]);
    setTaskSearchError(null);
    setLinkTaskAsPrimary(false);
    setLinkingTaskId(null);
    setPromotingTaskId(null);
    setUnlinkingTaskId(null);
    setIsCreateTaskModalOpen(false);
    setCreateTaskMessage(null);
  }, [selectedConv?.id]);

  React.useEffect(() => {
    setIsAiThinkingPanelOpen(false);
    setIsAiThinkingCompletionVisible(false);
    previousThinkingStepCountRef.current = 0;
    previousAiBusyRef.current = false;
    clearAiThinkingAutoHide();
  }, [clearAiThinkingAutoHide, selectedConv?.id]);

  React.useEffect(() => {
    setIsAiActivityPanelOpen(false);
    setIsAiSettingsPanelOpen(false);
    setIsAiMemorySectionExpanded(false);
    setAiActivityItems([]);
    setAiActivityError(null);
    setAiActivityHasMore(false);
    setAiActivityNextBefore(undefined);
    setAiActivityExpandedIds(new Set());
    setAiActivityRunIdFilter(null);
  }, [selectedConv?.id]);

  React.useEffect(() => {
    if (!selectedConv) {
      setIsAiThinkingPanelOpen(false);
      previousThinkingStepCountRef.current = 0;
      return;
    }

    const currentStepCount = aiThinkingSteps.length;
    if (currentStepCount === 0) {
      hideAiThinkingStatus();
      previousThinkingStepCountRef.current = 0;
      return;
    }

    const hasNewSteps = currentStepCount > previousThinkingStepCountRef.current;
    const latestStep = aiThinkingSteps[currentStepCount - 1];

    if (isAiThinkingTerminalStep(latestStep.step, latestStep.stepKey)) {
      if (hasNewSteps && previousThinkingStepCountRef.current > 0) {
        scheduleAiThinkingAutoHide();
      } else {
        hideAiThinkingStatus();
      }
    } else if (hasNewSteps && currentStepCount > 0) {
      clearAiThinkingAutoHide();
      setIsAiThinkingCompletionVisible(false);
      setIsAiThinkingPanelOpen(true);
    }

    previousThinkingStepCountRef.current = currentStepCount;
  }, [aiThinkingSteps, clearAiThinkingAutoHide, hideAiThinkingStatus, scheduleAiThinkingAutoHide, selectedConv]);

  React.useEffect(() => {
    if (aiDraftLoading || aiSummaryLoading) {
      clearAiThinkingAutoHide();
      setIsAiThinkingCompletionVisible(false);
      setIsAiThinkingPanelOpen(true);
    }
  }, [aiDraftLoading, aiSummaryLoading, clearAiThinkingAutoHide]);

  React.useEffect(() => {
    const isBusy = aiDraftLoading || aiSummaryLoading;
    const wasBusy = previousAiBusyRef.current;
    previousAiBusyRef.current = isBusy;

    if (wasBusy && !isBusy) {
      const latestStep = aiThinkingSteps.length > 0
        ? aiThinkingSteps[aiThinkingSteps.length - 1]
        : null;

      if (!latestStep || isAiThinkingTerminalStep(latestStep.step, latestStep.stepKey)) {
        scheduleAiThinkingAutoHide();
      }
    }
  }, [aiDraftLoading, aiSummaryLoading, aiThinkingSteps, scheduleAiThinkingAutoHide]);

  React.useEffect(() => () => {
    clearAiThinkingAutoHide();
  }, [clearAiThinkingAutoHide]);

  const loadAiActivityRef = React.useRef(loadAiActivity);
  loadAiActivityRef.current = loadAiActivity;

  React.useEffect(() => {
    if (!selectedConv?.id || !canViewAiHistorySettings || !isAiActivityPanelOpen) {
      return;
    }

    void loadAiActivityRef.current(selectedConv.id, false);
  }, [
    aiActivityPluginFilter,
    aiActivityRunIdFilter,
    aiActivityStatusFilter,
    aiActivityTriggeredByFilter,
    canViewAiHistorySettings,
    isAiActivityPanelOpen,
    selectedConv?.id,
  ]);

  React.useEffect(() => {
    if (!selectedConv?.id || !canManageTasks || !isTaskPickerOpen) {
      setTaskSearchResults([]);
      setTaskSearchError(null);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void (async () => {
        setTaskSearchLoading(true);
        setTaskSearchError(null);
        try {
          const result = await taskService.listTasks({
            page: 1,
            pageSize: 6,
            searchText: taskSearchTerm.trim() || undefined,
          });
          setTaskSearchResults(result.items);
        } catch {
          setTaskSearchError(t('failedToSearchTasks', { ns: 'conversations' }));
        } finally {
          setTaskSearchLoading(false);
        }
      })();
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [canManageTasks, isTaskPickerOpen, selectedConv?.id, t, taskSearchTerm]);

  const handleOpenTask = React.useCallback((taskId: string) => {
    navigate(`/tasks/${taskId}`);
  }, [navigate]);

  const handleLinkTask = React.useCallback(async (taskId: string) => {
    if (!selectedConv?.id) {
      return;
    }

    const conversationId = selectedConv.id;
    setLinkingTaskId(taskId);
    setTaskSearchError(null);

    try {
      const linkedTask = await linkExistingTaskToConversation(conversationId, taskId, {
        isPrimary: linkedTasks.length > 0 ? linkTaskAsPrimary : undefined,
      });

      if (!linkedTask) {
        throw new Error(t('failedToLinkTask', { ns: 'conversations' }));
      }

      setLinkedTasks(current => {
        const filtered = current.filter(item => item.id !== linkedTask.id);
        return [linkedTask, ...filtered].sort((left, right) => {
          const leftLink = getConversationTaskLink(left, conversationId);
          const rightLink = getConversationTaskLink(right, conversationId);
          return Number(rightLink?.isPrimary) - Number(leftLink?.isPrimary);
        });
      });
      setTaskSearchResults(current => current.filter(item => item.id !== linkedTask.id));
      setTaskSearchTerm('');
      setLinkTaskAsPrimary(false);
      setIsTaskPickerOpen(false);
      showToast(t('taskLinkedSuccessfully', { ns: 'conversations' }), 'success');
    } catch {
      const message = t('failedToLinkTask', { ns: 'conversations' });
      setTaskSearchError(message);
      showToast(message, 'error');
    } finally {
      setLinkingTaskId(null);
    }
  }, [linkTaskAsPrimary, linkedTasks.length, selectedConv?.id, showToast, t]);

  const handleMakeTaskPrimary = React.useCallback(async (taskId: string) => {
    if (!selectedConv?.id) {
      return;
    }

    setPromotingTaskId(taskId);
    setLinkedTasksError(null);

    try {
      const updatedTask = await linkExistingTaskToConversation(selectedConv.id, taskId, { isPrimary: true });
      if (!updatedTask) {
        throw new Error(t('failedToMakeTaskPrimary', { ns: 'conversations' }));
      }

      await loadLinkedTasks(selectedConv.id);
      showToast(t('taskMarkedPrimary', { ns: 'conversations' }), 'success');
    } catch {
      const message = t('failedToMakeTaskPrimary', { ns: 'conversations' });
      setLinkedTasksError(message);
      showToast(message, 'error');
    } finally {
      setPromotingTaskId(null);
    }
  }, [loadLinkedTasks, selectedConv?.id, showToast, t]);

  const handleUnlinkTask = React.useCallback(async (taskId: string) => {
    if (!selectedConv?.id) {
      return;
    }

    if (!window.confirm(t('unlinkTaskConfirm', { ns: 'conversations' }))) {
      return;
    }

    setUnlinkingTaskId(taskId);
    setLinkedTasksError(null);

    try {
      await taskService.unlinkTaskFromConversation(taskId, selectedConv.id);
      await loadLinkedTasks(selectedConv.id);
      showToast(t('taskUnlinkedSuccessfully', { ns: 'conversations' }), 'success');
    } catch {
      const message = t('failedToUnlinkTask', { ns: 'conversations' });
      setLinkedTasksError(message);
      showToast(message, 'error');
    } finally {
      setUnlinkingTaskId(null);
    }
  }, [loadLinkedTasks, selectedConv?.id, showToast, t]);

  const openCreateTaskModal = React.useCallback((message?: Message) => {
    setCreateTaskMessage(message ?? null);
    setIsCreateTaskModalOpen(true);
  }, []);

  const closeCreateTaskModal = React.useCallback(() => {
    setIsCreateTaskModalOpen(false);
    setCreateTaskMessage(null);
  }, []);

  const handleTaskCreated = React.useCallback(async () => {
    if (selectedConv?.id) {
      await loadLinkedTasks(selectedConv.id);
    }
    closeCreateTaskModal();
  }, [closeCreateTaskModal, loadLinkedTasks, selectedConv?.id]);

  const ensureParticipantOptionsLoaded = React.useCallback(async () => {
    if (participantOptions.length > 0 || participantOptionsLoading) {
      return;
    }

    setParticipantOptionsLoading(true);
    setParticipantOptionsError(null);
    try {
      const options = await getParticipantOptions();
      setParticipantOptions(options);
    } catch (e) {
      setParticipantOptionsError(e instanceof Error ? e.message : t('failedToLoadParticipants', { ns: 'conversations' }));
    } finally {
      setParticipantOptionsLoading(false);
    }
  }, [participantOptions.length, participantOptionsLoading, t]);

  const directConversationParticipant = React.useMemo(() => {
    if (!selectedConv) {
      return null;
    }

    const nonAiParticipants = selectedConv.participants.filter(participant => participant.type !== 'aiAssistant');
    if (nonAiParticipants.length !== 2) {
      return null;
    }

    const externalParticipant = nonAiParticipants.find(participant => participant.type === 'customer' || participant.type === 'lead');
    if (externalParticipant) {
      return externalParticipant;
    }

    if (meSender) {
      const otherParticipant = nonAiParticipants.find(
        participant => !(participant.type === meSender.type && participant.id === meSender.id),
      );
      if (otherParticipant) {
        return otherParticipant;
      }
    }

    return nonAiParticipants[0] ?? null;
  }, [meSender, selectedConv]);

  const directParticipantOption = React.useMemo(() => {
    if (!directConversationParticipant) {
      return undefined;
    }

    return participantOptionByKey.get(`${directConversationParticipant.type}:${directConversationParticipant.id}`);
  }, [directConversationParticipant, participantOptionByKey]);

  const directParticipantAvatarTitle = React.useMemo(() => {
    if (!selectedConv || !directConversationParticipant) {
      return '';
    }

    return getConversationDisplayName(selectedConv);
  }, [directConversationParticipant, getConversationDisplayName, selectedConv]);

  const directParticipantAvatarInitials = React.useMemo(() => {
    const words = directParticipantAvatarTitle
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2);

    if (words.length === 0) {
      return '?';
    }

    return words.map(word => word.charAt(0).toUpperCase()).join('');
  }, [directParticipantAvatarTitle]);

  const resolveParticipantAvatarUrl = React.useCallback((participant: ConversationParticipantRef | ConversationParticipantOption) => {
    if (participant.type === 'aiAssistant') {
      return AI_ASSISTANT_AVATAR_URL;
    }

    if (meSender && participant.type === 'companyMember' && participant.id === meSender.id && currentEmployeeAvatarUrl) {
      return currentEmployeeAvatarUrl;
    }

    const directOption = participantOptionByKey.get(`${participant.type}:${participant.id}`);
    const participantAvatarUrl = 'avatarUrl' in participant ? participant.avatarUrl : undefined;
    const participantContactId = 'contactId' in participant ? participant.contactId : undefined;

    if (participantAvatarUrl || directOption?.avatarUrl) {
      return participantAvatarUrl || directOption?.avatarUrl;
    }

    const normalizedParticipantName = participant.name?.trim().toLocaleLowerCase();
    const matchingConversationParticipant = selectedConv?.participants.find(candidate => {
      if (candidate.type !== participant.type) {
        return false;
      }

      if (candidate.id === participant.id) {
        return true;
      }

      if (participantContactId && candidate.id === participantContactId) {
        return true;
      }

      if (candidate.contactId && candidate.contactId === participant.id) {
        return true;
      }

      if (participantContactId && candidate.contactId && candidate.contactId === participantContactId) {
        return true;
      }

      return Boolean(
        normalizedParticipantName
        && candidate.name?.trim()
        && candidate.name.trim().toLocaleLowerCase() === normalizedParticipantName,
      );
    });

    if (!matchingConversationParticipant) {
      return undefined;
    }

    const lookupKeys = [
      `${matchingConversationParticipant.type}:${matchingConversationParticipant.id}`,
      matchingConversationParticipant.contactId ? `${matchingConversationParticipant.type}:${matchingConversationParticipant.contactId}` : null,
      participantContactId ? `${participant.type}:${participantContactId}` : null,
    ].filter((value): value is string => Boolean(value));

    for (const lookupKey of lookupKeys) {
      const matchedOption = participantOptionByKey.get(lookupKey);
      if (matchedOption?.avatarUrl) {
        return matchedOption.avatarUrl;
      }
    }

    return undefined;
  }, [currentEmployeeAvatarUrl, meSender, participantOptionByKey, selectedConv]);

  const resolveParticipantChannelIcon = React.useCallback((
    participant: ConversationParticipantRef | ConversationParticipantOption,
  ) => {
    if (participant.type !== 'customer' && participant.type !== 'lead') {
      return null;
    }

    if (resolveParticipantAvatarUrl(participant)) {
      return null;
    }

    const directOption = participantOptionByKey.get(`${participant.type}:${participant.id}`);
    const participantContactId = 'contactId' in participant ? participant.contactId : undefined;
    const resolvedParticipant: ConversationParticipantRef = {
      type: participant.type,
      id: participant.id,
      name: participant.name,
      contactId: participantContactId,
      contactChannelType: ('contactChannelType' in participant ? participant.contactChannelType : undefined)
        ?? directOption?.contactChannelType,
      contactChannelLabel: ('contactChannelLabel' in participant ? participant.contactChannelLabel : undefined)
        ?? directOption?.contactChannelLabel,
      contactChannelValue: ('contactChannelValue' in participant ? participant.contactChannelValue : undefined)
        ?? directOption?.contactChannelValue,
    };

    return getParticipantChannelIcon(resolvedParticipant, t)
      ?? (selectedConv ? resolveExternalConversationAvatarIcon(selectedConv, t, resolvedParticipant) : null);
  }, [participantOptionByKey, resolveParticipantAvatarUrl, selectedConv, t]);

  const renderParticipantIdentityAvatar = React.useCallback((
    participant: ConversationParticipantRef | ConversationParticipantOption,
    className: string,
  ) => {
    const avatarUrl = resolveParticipantAvatarUrl(participant);

    if (avatarUrl) {
      return (
        <span className={className} aria-hidden="true">
          <img src={avatarUrl} alt="" className={detailsStyles.participantAvatarImage} loading="lazy" />
        </span>
      );
    }

    const channelIcon = resolveParticipantChannelIcon(participant);
    if (channelIcon) {
      return (
        <span className={className} aria-hidden="true">
          <ConversationChannelBadge
            icon={channelIcon}
            title={channelIcon.label}
            size="messageAvatar"
            disableTooltip
          />
        </span>
      );
    }

    return <ParticipantAvatar participantType={participant.type} className={className} />;
  }, [resolveParticipantAvatarUrl, resolveParticipantChannelIcon]);

  const headerChannelIcons = React.useMemo(() => {
    if (!selectedConv) {
      return [];
    }

    return getConversationHeaderChannelIcons(selectedConv, t);
  }, [selectedConv, t]);

  const conversationAccountChannel = React.useMemo(
    () => channelIconKindToAccountChannel(headerChannelIcons[0]?.kind),
    [headerChannelIcons],
  );
  const channelIsConfigured = useChannelReadiness(conversationAccountChannel);
  const unconfiguredChannelLabel = conversationAccountChannel && channelIsConfigured === false
    ? CHANNEL_DISPLAY_NAMES[conversationAccountChannel]
    : null;

  const headerChannelIconTitles = React.useMemo(() => {
    if (!selectedConv) {
      return new Map<string, string>();
    }

    return getConversationChannelIconTitles(selectedConv, t, { maskChannelValues });
  }, [maskChannelValues, selectedConv, t]);

  const headerFallbackChannelAvatarIcon = React.useMemo(() => {
    if (!selectedConv || !isSelectedConversationExternal) {
      return null;
    }

    if (directParticipantOption?.avatarUrl) {
      return null;
    }

    return resolveExternalConversationAvatarIcon(
      selectedConv,
      t,
      directConversationParticipant ?? null,
    );
  }, [directConversationParticipant, directParticipantOption?.avatarUrl, isSelectedConversationExternal, selectedConv, t]);

  React.useEffect(() => {
    if (!selectedConv) {
      return;
    }

    void ensureParticipantOptionsLoaded();
  }, [ensureParticipantOptionsLoaded, selectedConv]);

  React.useEffect(() => {
    let active = true;

    if (!auth?.employeeId || meSender?.type !== 'companyMember') {
      setCurrentEmployeeAvatarUrl(undefined);
      return () => {
        active = false;
      };
    }

    const loadCurrentEmployeeAvatar = async () => {
      try {
        const response = await getCurrentEmployee();
        if (active) {
          setCurrentEmployeeAvatarUrl(response?.employee?.profileImageUrl || undefined);
        }
      } catch {
        if (active) {
          setCurrentEmployeeAvatarUrl(undefined);
        }
      }
    };

    void loadCurrentEmployeeAvatar();

    return () => {
      active = false;
    };
  }, [auth?.employeeId, meSender?.id, meSender?.type]);

  React.useEffect(() => () => {
    if (highlightTimeoutRef.current !== null) {
      window.clearTimeout(highlightTimeoutRef.current);
    }
  }, []);

  const onJumpToMessage = React.useCallback(async (messageId?: string | null) => {
    if (!messageId) {
      return;
    }

    const highlightMessage = (targetId: string) => {
      const element = document.getElementById(`conversation-msg-${targetId}`);
      if (!element) {
        return false;
      }

      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedMessageId(targetId);

      if (highlightTimeoutRef.current !== null) {
        window.clearTimeout(highlightTimeoutRef.current);
      }

      highlightTimeoutRef.current = window.setTimeout(() => {
        setHighlightedMessageId(current => current === targetId ? null : current);
        highlightTimeoutRef.current = null;
      }, 1800);

      return true;
    };

    if (highlightMessage(messageId)) {
      return;
    }

    const loaded = await onLoadOlderMessagesForJump(messageId);
    if (!loaded) {
      return;
    }

    window.setTimeout(() => {
      highlightMessage(messageId);
    }, 0);
  }, [onLoadOlderMessagesForJump]);

  const updateInteractiveList = React.useCallback((updater: (current: ConversationInteractiveList) => ConversationInteractiveList) => {
    setInteractiveListDraft(current => updater(current));
  }, [setInteractiveListDraft]);

  const updateInteractiveButtons = React.useCallback((updater: (current: ConversationInteractiveButtons) => ConversationInteractiveButtons) => {
    setInteractiveButtonsDraft(current => updater(current));
  }, [setInteractiveButtonsDraft]);

  const addInteractiveSection = React.useCallback(() => {
    updateInteractiveList(current => ({
      ...current,
      sections: [...current.sections, { title: '', rows: [{ id: '', title: '', description: '' }] }],
    }));
  }, [updateInteractiveList]);

  const removeInteractiveSection = React.useCallback((sectionIndex: number) => {
    setCollapsedInteractiveSections(current => {
      const next = new Set<number>();
      current.forEach(index => {
        if (index < sectionIndex) {
          next.add(index);
        } else if (index > sectionIndex) {
          next.add(index - 1);
        }
      });
      return next;
    });
    updateInteractiveList(current => ({
      ...current,
      sections: current.sections.filter((_, index) => index !== sectionIndex),
    }));
  }, [updateInteractiveList]);

  const toggleInteractiveSection = React.useCallback((sectionIndex: number) => {
    setCollapsedInteractiveSections(current => {
      const next = new Set(current);
      if (next.has(sectionIndex)) {
        next.delete(sectionIndex);
      } else {
        next.add(sectionIndex);
      }
      return next;
    });
  }, []);

  React.useEffect(() => {
    setCollapsedInteractiveSections(current => {
      const next = new Set<number>();
      current.forEach(index => {
        if (index >= 0 && index < interactiveListDraft.sections.length) {
          next.add(index);
        }
      });
      return next.size === current.size ? current : next;
    });
  }, [interactiveListDraft.sections.length]);

  const addInteractiveRow = React.useCallback((sectionIndex: number) => {
    updateInteractiveList(current => ({
      ...current,
      sections: current.sections.map((section, index) => index === sectionIndex
        ? { ...section, rows: [...section.rows, { id: '', title: '', description: '' }] }
        : section),
    }));
  }, [updateInteractiveList]);

  const removeInteractiveRow = React.useCallback((sectionIndex: number, rowIndex: number) => {
    updateInteractiveList(current => ({
      ...current,
      sections: current.sections.map((section, index) => index === sectionIndex
        ? { ...section, rows: section.rows.filter((_, candidateIndex) => candidateIndex !== rowIndex) }
        : section),
    }));
  }, [updateInteractiveList]);

  const addInteractiveButton = React.useCallback(() => {
    updateInteractiveButtons(current => ({
      ...current,
      buttons: isInteractiveButtonAddDisabled(current.buttons.length, interactiveButtonMaxCount)
        ? current.buttons
        : [...current.buttons, { id: '', title: '' }],
    }));
  }, [interactiveButtonMaxCount, updateInteractiveButtons]);

  const removeInteractiveButton = React.useCallback((buttonIndex: number) => {
    updateInteractiveButtons(current => ({
      ...current,
      buttons: current.buttons.filter((_, candidateIndex) => candidateIndex !== buttonIndex),
    }));
  }, [updateInteractiveButtons]);

  React.useEffect(() => {
    setIsScheduledPanelExpanded(false);
    closeParticipantsMenu();
    setIsAddParticipantsOpen(false);
    setParticipantSearchTerm('');
    setSelectedParticipantKeys(new Set());
    setAddParticipantsError(null);
  }, [closeParticipantsMenu, selectedId]);

  React.useEffect(() => {
    if (expandScheduledMessagesSignal > 0) {
      setIsScheduledPanelExpanded(true);
    }
  }, [expandScheduledMessagesSignal]);

  React.useEffect(() => {
    if (!isScheduleMenuOpen) {
      return;
    }

    const onPointerDown = (event: MouseEvent) => {
      if (!scheduleMenuRef.current?.contains(event.target as Node)) {
        setIsScheduleMenuOpen(false);
        setShowCustomScheduleInput(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isScheduleMenuOpen]);

  const resolveParticipantLink = React.useCallback((participant: ConversationParticipantRef): string | null => {
    if (!participant.id) {
      return null;
    }

    if (participant.type === 'companyMember') {
      return `/members/${participant.id}`;
    }

    if (participant.type === 'customer' || participant.type === 'lead') {
      const contactId = participant.contactId?.trim() || participant.id;
      return contactId ? `/contacts/${contactId}` : null;
    }

    return null;
  }, []);

  const onParticipantClick = React.useCallback((participant: ConversationParticipantRef) => {
    const target = resolveParticipantLink(participant);
    if (!target) {
      return;
    }

    closeParticipantsMenu();
    navigate(target);
  }, [closeParticipantsMenu, navigate, resolveParticipantLink]);

  const participantCount = selectedConv?.participants.length || 0;
  const shouldUseParticipantsPopup = participantCount > 2;
  const visibleNoteCount = selectedConv?.visibleNoteCount || 0;
  const linkedTaskCount = linkedTasks.length;

  const currentParticipantKeySet = React.useMemo(() => {
    return new Set((selectedConv?.participants || []).map(p => `${p.type}:${p.id}`));
  }, [selectedConv?.participants]);

  const availableParticipantOptions = React.useMemo(() => {
    return participantOptions
      .filter(option => option && option.id && option.type)
      .filter(option => !currentParticipantKeySet.has(`${option.type}:${option.id}`));
  }, [currentParticipantKeySet, participantOptions]);

  const filteredParticipantOptions = React.useMemo(() => {
    const query = participantSearchTerm.trim().toLowerCase();
    if (!query) {
      return availableParticipantOptions;
    }

    return availableParticipantOptions.filter(option =>
      (option.name || '').toLowerCase().includes(query) ||
      (option.email || '').toLowerCase().includes(query) ||
      option.id.toLowerCase().includes(query) ||
      option.type.toLowerCase().includes(query)
    );
  }, [availableParticipantOptions, participantSearchTerm]);

  const canAddParticipants = React.useMemo(() => {
    return Boolean(selectedConv && getConversationKind(selectedConv) === 'internal-channel');
  }, [selectedConv]);

  const selectedConversationKind = React.useMemo(() => {
    return selectedConv ? getConversationKind(selectedConv) : 'unknown';
  }, [selectedConv]);

  const headerDisplayName = selectedConv ? getConversationDisplayName(selectedConv) : '';

  const headerAvatarInitials = React.useMemo(() => {
    const words = headerDisplayName
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2);

    if (words.length === 0) {
      return '?';
    }

    return words.map(word => word.charAt(0).toUpperCase()).join('');
  }, [headerDisplayName]);

  React.useEffect(() => {
    if (!isAddParticipantsOpen || participantOptionsLoading || participantOptionsError || availableParticipantOptions.length === 0) {
      return;
    }

    participantSearchInputRef.current?.focus();
  }, [availableParticipantOptions.length, isAddParticipantsOpen, participantOptionsError, participantOptionsLoading]);

  const openAddParticipants = React.useCallback(async () => {
    if (!canAddParticipants) {
      return;
    }

    setIsAddParticipantsOpen(true);
    setParticipantSearchTerm('');
    setSelectedParticipantKeys(new Set());
    setAddParticipantsError(null);
    await ensureParticipantOptionsLoaded();
  }, [canAddParticipants, ensureParticipantOptionsLoaded]);

  const toggleSelectedParticipant = React.useCallback((key: string) => {
    setSelectedParticipantKeys(current => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const submitAddParticipants = React.useCallback(async () => {
    if (!selectedConv || !canAddParticipants || selectedParticipantKeys.size === 0 || addingParticipants) return;

    setAddingParticipants(true);
    setAddParticipantsError(null);
    try {
      const participantRefs: ConversationParticipantRef[] = Array.from(selectedParticipantKeys).map(key => {
        const [type, id] = key.split(':');
        return { type: type as ParticipantType, id };
      });

      const updated = await addConversationParticipants(selectedConv.id, participantRefs);
      if (updated) {
        setIsAddParticipantsOpen(false);
        setSelectedParticipantKeys(new Set());
      } else {
        setAddParticipantsError(t('conversationNotFound', { ns: 'conversations' }));
      }
    } catch (e) {
      setAddParticipantsError(e instanceof Error ? e.message : t('failedToAddParticipants', { ns: 'conversations' }));
    } finally {
      setAddingParticipants(false);
    }
  }, [addingParticipants, canAddParticipants, selectedConv, selectedParticipantKeys, t]);

  const handleScheduleAt = async (value: Date) => {
    await onScheduleAt(value);
    setIsScheduleMenuOpen(false);
    setShowCustomScheduleInput(false);
    setCustomScheduleValue('');
  };

  const buildLaterTodayPreset = () => {
    const next = new Date();
    next.setHours(13, 0, 0, 0);
    if (next <= new Date()) {
      next.setDate(next.getDate() + 1);
    }
    return next;
  };

  const buildMondayMorningPreset = () => {
    const now = new Date();
    const daysUntilMonday = (8 - now.getDay()) % 7 || 7;
    const nextMonday = new Date(now);
    nextMonday.setDate(now.getDate() + daysUntilMonday);
    nextMonday.setHours(9, 0, 0, 0);
    return nextMonday;
  };

  return (
    <div
      ref={detailsPaneRef}
      className={[
        pageStyles.detailsPane,
        isListExpanded && !isMobileLayout ? pageStyles.collapsed : '',
        isDetailsMaximized ? pageStyles.detailsMaximized : '',
        isMobileLayout && !selectedId ? pageStyles.mobileHidden : '',
        isMobileLayout && selectedId ? pageStyles.mobileFullWidth : '',
      ].filter(Boolean).join(' ')}
    >
      {!selectedId && (
        <div className={pageStyles.placeholder}>{t('selectConversationFromList', { ns: 'conversations' })}</div>
      )}
      {selectedId && detailsLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}><Spinner text={t('loadingConversation', { ns: 'conversations' })} /></div>
      )}
      {selectedId && detailsError && (
        <div className="error" style={{ marginTop: 12 }}>{detailsError}</div>
      )}
      {selectedConv && !detailsLoading && !detailsError && (
        <div
          className={[
            detailsStyles.wrapper,
            dragActive ? detailsStyles.wrapperDragActive : '',
          ].filter(Boolean).join(' ')}
          onDragEnter={onComposerDragOver}
          onDragOver={onComposerDragOver}
          onDragLeave={onComposerDragLeave}
          onDrop={onComposerDrop}
        >
          <div className={`${detailsStyles.header} ${isMobileLayout ? detailsStyles.headerMobile : ''}`} ref={filesPanelRef}>
            <div className={detailsStyles.headerTopLine}>
              {isMobileLayout && (
                <button
                  type="button"
                  className={detailsStyles.mobileBackBtn}
                  onClick={onBackToList}
                  aria-label={t('backToList', { ns: 'conversations' })}
                >
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M14 6L8 12L14 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
              <div className={detailsStyles.headerIdentity}>
                {selectedConv && isAiAssistantDirectConversation(selectedConv) ? (
                  <span
                    className={detailsStyles.headerAvatar}
                    title={directParticipantAvatarTitle}
                    aria-hidden="true"
                  >
                    <img src={AI_ASSISTANT_AVATAR_URL} alt="" className={detailsStyles.headerAvatarImage} loading="lazy" />
                  </span>
                ) : selectedConv?.whatsAppGroupPictureUrl ? (
                  <span className={detailsStyles.headerAvatar} title={headerDisplayName} aria-hidden="true">
                    <img src={selectedConv.whatsAppGroupPictureUrl} alt="" className={detailsStyles.headerAvatarImage} loading="lazy" />
                  </span>
                ) : directConversationParticipant ? (
                  headerFallbackChannelAvatarIcon ? (
                    <ConversationChannelBadge
                      icon={headerFallbackChannelAvatarIcon}
                      title={headerChannelIconTitles.get(headerFallbackChannelAvatarIcon.key) ?? headerFallbackChannelAvatarIcon.label}
                      size="headerAvatar"
                      disableTooltip
                    />
                  ) : (
                  <span
                    className={detailsStyles.headerAvatar}
                    title={directParticipantAvatarTitle}
                    style={directParticipantOption?.avatarColor ? { backgroundColor: directParticipantOption.avatarColor } : undefined}
                    aria-hidden="true"
                  >
                    {directParticipantOption?.avatarUrl ? (
                      <img src={directParticipantOption.avatarUrl} alt="" className={detailsStyles.headerAvatarImage} loading="lazy" />
                    ) : (
                      <span className={detailsStyles.headerAvatarFallback}>{directParticipantAvatarInitials}</span>
                    )}
                  </span>
                  )
                ) : selectedConversationKind === 'internal-channel' ? (
                  <span className={`${detailsStyles.headerAvatar} ${detailsStyles.headerAvatarChannel}`} aria-hidden="true">
                    <span className={detailsStyles.headerAvatarChannelMark}>#</span>
                  </span>
                ) : headerFallbackChannelAvatarIcon ? (
                  <ConversationChannelBadge
                    icon={headerFallbackChannelAvatarIcon}
                    title={headerChannelIconTitles.get(headerFallbackChannelAvatarIcon.key) ?? headerFallbackChannelAvatarIcon.label}
                    size="headerAvatar"
                    disableTooltip
                  />
                ) : (
                  <span
                    className={`${detailsStyles.headerAvatar} ${isSelectedConversationExternal ? detailsStyles.headerAvatarExternal : ''}`}
                    aria-hidden="true"
                  >
                    <span className={detailsStyles.headerAvatarFallback}>{headerAvatarInitials}</span>
                  </span>
                )}

                <div className={detailsStyles.headerIdentityText}>
                  <div className={detailsStyles.headerTitleRow}>
                    <h2 className={detailsStyles.title}>{headerDisplayName}</h2>
                    {headerChannelIcons.length > 0 && !headerFallbackChannelAvatarIcon && (
                      <span className={detailsStyles.headerTitleChannelBadges}>
                        {headerChannelIcons.map(icon => (
                          <ConversationChannelBadge
                            key={icon.key}
                            icon={icon}
                            title={headerChannelIconTitles.get(icon.key) ?? icon.label}
                            compact
                            inlineLabel={getChannelBadgeInlineLabel(icon.kind)}
                          />
                        ))}
                      </span>
                    )}
                  </div>
                  {selectedConv.channelAccountDisplayName ? (
                    <div className={detailsStyles.headerSubtitle}>
                      <span
                        className={`${detailsStyles.metaPill} ${detailsStyles.channelAccountPill}`}
                        title={t('channelAccountBadgeTitle', { ns: 'conversations', name: selectedConv.channelAccountDisplayName })}
                      >
                        {t('channelAccountBadgeLabel', { ns: 'conversations', name: selectedConv.channelAccountDisplayName })}
                      </span>
                    </div>
                  ) : null}
                  {isMessengerCommentThread ? (
                    <div className={detailsStyles.headerSubtitle}>
                      <span
                        className={`${detailsStyles.metaPill} ${detailsStyles.instagramCommentThreadPill}`}
                        title={t('messengerCommentThreadLabel', { ns: 'conversations' })}
                      >
                        {t('messengerCommentThreadLabel', { ns: 'conversations' })}
                      </span>
                    </div>
                  ) : null}
                  {isInstagramCommentThread ? (
                    <div className={detailsStyles.headerSubtitle}>
                      <span
                        className={`${detailsStyles.metaPill} ${detailsStyles.instagramCommentThreadPill}`}
                        title={t('instagramCommentThreadLabel', { ns: 'conversations' })}
                      >
                        {t('instagramCommentThreadLabel', { ns: 'conversations' })}
                      </span>
                    </div>
                  ) : null}
                  {isWhatsAppGroup ? (
                    <div className={detailsStyles.headerSubtitle}>
                      <span className={detailsStyles.metaPill}>
                        {t('whatsAppGroup', { ns: 'conversations', defaultValue: 'WhatsApp group' })}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className={detailsStyles.headerActions}>
                {(showAssigneeSelector || selectedConv.assignedToEmployeeName || (isSelectedConversationExternal && !selectedConv.isSigned)) ? (
                  <div className={detailsStyles.headerConversationControls}>
                    {showAssigneeSelector ? (
                      <ConversationAssigneePicker
                        employeeOptions={employeeOptions}
                        assignedToEmployeeId={selectedConv.assignedToEmployeeId}
                        assignedToEmployeeName={selectedConv.assignedToEmployeeName}
                        isUpdating={isAssigneeUpdating}
                        onAssign={(employeeId) => {
                          if (!selectedId) {
                            return;
                          }

                          void onAssignConversation(selectedId, employeeId);
                        }}
                        onUnassign={() => {
                          if (!selectedId) {
                            return;
                          }

                          void onUnassignConversation(selectedId);
                        }}
                      />
                    ) : selectedConv.assignedToEmployeeName ? (
                      <span className={`${detailsStyles.metaPill} ${detailsStyles.assigneePill}`}>
                        <span className={detailsStyles.assigneePillLabel}>{t('assigneeLabel', { ns: 'conversations' })}</span>
                        <span>{selectedConv.assignedToEmployeeName}</span>
                      </span>
                    ) : null}
                    {isSelectedConversationExternal && !selectedConv.isSigned ? (
                      <button
                        type="button"
                        className={detailsStyles.signBtnInline}
                        onClick={onSign}
                        disabled={signing}
                      >
                        {signing ? t('signing', { ns: 'conversations' }) : t('signConversation', { ns: 'conversations' })}
                      </button>
                    ) : null}
                  </div>
                ) : null}
                <div className={detailsStyles.headerQuickActions}>
                {selectedConv && (
                  <AiVoiceCallControl conversation={selectedConv} />
                )}
                {selectedConv && (
                  <ConversationRequestPaymentButton conversation={selectedConv} />
                )}
                {selectedId && (
                  <button
                    type="button"
                    className={detailsStyles.focusModeButton}
                    onClick={() => {
                      void onToggleDetailsMaximized();
                    }}
                    aria-label={isDetailsMaximized
                      ? t('restoreSplitView', { ns: 'conversations' })
                      : t('maximizeConversation', { ns: 'conversations' })}
                    title={isDetailsMaximized
                      ? t('restoreSplitView', { ns: 'conversations' })
                      : t('maximizeConversation', { ns: 'conversations' })}
                    aria-pressed={isDetailsMaximized}
                  >
                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      {isDetailsMaximized ? (
                        <>
                          <path d="M6.25 5.75H4.75V12.25H11.25V10.75" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M9.25 8.25H15.75V14.75H9.25V8.25Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </>
                      ) : (
                        <>
                          <path d="M3.75 8.25V3.75H8.25" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M11.75 3.75H16.25V8.25" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M3.75 11.75V16.25H8.25" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M16.25 11.75V16.25H11.75" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </>
                      )}
                    </svg>
                  </button>
                )}
                {canViewAiHistorySettings && (
                  <div className={detailsStyles.metaSettingsWrap} ref={aiHistorySettingsRef}>
                    <button
                      type="button"
                      className={detailsStyles.metaSettingsButton}
                      aria-label={t('aiHistoryStartAtUtcMenuLabel', { ns: 'conversations' })}
                      title={selectedConv.aiHistoryStartAtUtc
                        ? t('aiHistoryStartAtUtcCurrent', { ns: 'conversations', timestamp: formatDateTime(selectedConv.aiHistoryStartAtUtc) })
                        : t('aiHistoryStartAtUtcUnset', { ns: 'conversations' })}
                      onClick={() => setIsAiHistorySettingsOpen(current => !current)}
                      disabled={aiHistoryStartSaving}
                    >
                      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d="M8.3 2.2h3.4l.5 1.9a6 6 0 0 1 1.2.5l1.8-.9 2.4 2.4-.9 1.8c.2.4.4.8.5 1.2l1.9.5v3.4l-1.9.5a6 6 0 0 1-.5 1.2l.9 1.8-2.4 2.4-1.8-.9a6 6 0 0 1-1.2.5l-.5 1.9H8.3l-.5-1.9a6 6 0 0 1-1.2-.5l-1.8.9-2.4-2.4.9-1.8a6 6 0 0 1-.5-1.2l-1.9-.5V8.7l1.9-.5c.1-.4.3-.8.5-1.2l-.9-1.8 2.4-2.4 1.8.9c.4-.2.8-.4 1.2-.5l.5-1.9Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
                        <circle cx="10" cy="10" r="2.3" stroke="currentColor" strokeWidth="1.2"/>
                      </svg>
                    </button>
                    {isAiHistorySettingsOpen && (
                      <div className={detailsStyles.metaSettingsMenu} role="menu" aria-label={t('aiHistoryStartAtUtcMenuLabel', { ns: 'conversations' })}>
                        <button
                          type="button"
                          role="menuitem"
                          className={detailsStyles.metaSettingsMenuItem}
                          onClick={() => {
                            setIsAiHistorySettingsOpen(false);
                            void onUpdateAiHistoryStartAtUtc(new Date().toISOString());
                          }}
                          disabled={aiHistoryStartSaving}
                        >
                          {t('setAiHistoryStartAtUtcNow', { ns: 'conversations' })}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                </div>
              </div>
            </div>
            {unconfiguredChannelLabel && (
              <div className={detailsStyles.channelUnconfiguredBanner} role="status">
                <span className={detailsStyles.channelUnconfiguredIcon} aria-hidden="true">!</span>
                <span>
                  <strong className={detailsStyles.channelUnconfiguredTitle}>
                    {t('channelNotConfiguredTitle', { ns: 'conversations', channel: unconfiguredChannelLabel })}
                  </strong>
                  {' '}
                  {t('channelNotConfiguredHelp', { ns: 'conversations' })}
                </span>
              </div>
            )}
            {notificationPermission === 'denied' && (
              <div className={detailsStyles.metaRibbon}>
                <span className={`${detailsStyles.metaPill} ${detailsStyles.warningPill}`}>
                  {t('notificationsBlocked', { ns: 'conversations' })}
                </span>
              </div>
            )}
            <div className={detailsStyles.headerControls}>
              <div className={detailsStyles.headerParticipantsRow}>
              <div className={detailsStyles.participants} ref={participantsMenuRef}>
                {canAddParticipants && (
                  <button
                    type="button"
                    className={`${detailsStyles.chip} ${detailsStyles.chipButton} ${detailsStyles.chipClickable} ${detailsStyles.addParticipantChip}`}
                    title={t('addParticipants', { ns: 'conversations' })}
                    onClick={() => void openAddParticipants()}
                    disabled={participantOptionsLoading || addingParticipants}
                  >
                    <span className={detailsStyles.addParticipantPlus}>+</span>
                    <span>{t('addParticipants', { ns: 'conversations' })}</span>
                  </button>
                )}
                {shouldUseParticipantsPopup ? (
                  <>
                    <button
                      ref={participantsToggleRef}
                      type="button"
                      className={`${detailsStyles.chip} ${detailsStyles.chipButton} ${detailsStyles.participantsToggleButton}`}
                      onClick={toggleParticipantsMenu}
                      aria-expanded={isParticipantsMenuOpen}
                      aria-haspopup="menu"
                    >
                      <span>{t('participantsWithCount', { ns: 'conversations', count: participantCount })}</span>
                      <span className={`${detailsStyles.participantsChevron} ${isParticipantsMenuOpen ? detailsStyles.participantsChevronOpen : ''}`}>▾</span>
                    </button>
                    {isParticipantsMenuOpen && participantsPopoverStyle ? createPortal(
                      <div
                        ref={participantsPopoverRef}
                        className={detailsStyles.participantsPopover}
                        style={participantsPopoverStyle}
                        role="menu"
                        aria-label={t('conversationParticipants', { ns: 'conversations' })}
                      >
                        {selectedConv.participants.map(participant => {
                          const participantName = getParticipantDisplayName(participant);
                          const destination = resolveParticipantLink(participant);
                          const participantIcons = getParticipantConversationIcons(participant, t);
                          const participantIconTitles = getParticipantConversationIconTitles(participant, t, { maskChannelValues });
                          const participantChannelSummary = getParticipantChannelSummary(participant, t, { maskChannelValues });

                          return (
                            <button
                              type="button"
                              key={`${participant.type}-${participant.id}`}
                              className={`${detailsStyles.participantMenuItem} ${destination ? detailsStyles.participantMenuItemClickable : ''}`}
                              onClick={() => onParticipantClick(participant)}
                              disabled={!destination}
                              title={destination ? t('openParticipant', { ns: 'conversations', name: participantName }) : t('participantHasNoDetails', { ns: 'conversations', name: participantName })}
                            >
                              {renderParticipantIdentityAvatar(participant, detailsStyles.avatar)}
                              <span className={detailsStyles.participantMenuText}>
                                <span>{participantName}</span>
                                {participantIcons.length > 0 ? (
                                  <span className={detailsStyles.participantMenuBadges}>
                                    {participantIcons.map(icon => (
                                      <ConversationChannelBadge
                                        key={`${participant.type}-${participant.id}-${icon.key}`}
                                        icon={icon}
                                        title={participantIconTitles.get(icon.key) ?? icon.label}
                                        compact
                                      />
                                    ))}
                                  </span>
                                ) : null}
                                {participantChannelSummary ? (
                                  <span className={detailsStyles.participantMenuMeta}>{participantChannelSummary}</span>
                                ) : null}
                              </span>
                            </button>
                          );
                        })}
                      </div>,
                      document.body,
                    ) : null}
                  </>
                ) : (
                  selectedConv.participants.map(participant => {
                    const participantName = getParticipantDisplayName(participant);
                    const destination = resolveParticipantLink(participant);

                    return (
                      <button
                        type="button"
                        key={`${participant.type}-${participant.id}`}
                        className={`${detailsStyles.chip} ${detailsStyles.chipButton} ${destination ? detailsStyles.chipClickable : ''}`}
                        onClick={() => onParticipantClick(participant)}
                        disabled={!destination}
                        title={destination ? t('openParticipant', { ns: 'conversations', name: participantName }) : t('participantHasNoDetails', { ns: 'conversations', name: participantName })}
                      >
                        {renderParticipantIdentityAvatar(participant, detailsStyles.avatar)}
                        <span>{participantName}</span>
                      </button>
                    );
                  })
                )}
              </div>
              </div>
              <div className={detailsStyles.headerToolGroup}>
              <div className={detailsStyles.filesMenuWrap}>
                <button
                  type="button"
                  className={`${detailsStyles.filesToggleBtn} ${showFilesPanel ? detailsStyles.filesToggleBtnActive : ''}`}
                  onClick={() => setShowFilesPanel(current => !current)}
                  aria-expanded={showFilesPanel}
                  aria-label={showFilesPanel
                    ? t('hideFiles', { ns: 'conversations' })
                    : t('showFilesInConversation', { ns: 'conversations' })}
                  title={showFilesPanel
                    ? t('hideFiles', { ns: 'conversations' })
                    : t('showFilesInConversation', { ns: 'conversations' })}
                >
                  {renderFilesPanelIcon(detailsStyles.filesToggleIcon)}
                  <span>{showFilesPanel
                    ? t('hideFilesShort', { ns: 'conversations' })
                    : t('files', { ns: 'conversations' })}</span>
                  <span className={detailsStyles.filesCount}>{conversationFiles.length}</span>
                </button>
              </div>
              <button
                type="button"
                className={detailsStyles.filesToggleBtn}
                title={t('scheduledMessages', { ns: 'conversations' })}
                onClick={() => setIsScheduledPanelExpanded(current => !current)}
                aria-expanded={isScheduledPanelExpanded}
              >
                <span>{t('scheduled', { ns: 'conversations' })}</span>
                <span className={detailsStyles.filesCount}>{scheduledMessages.length}</span>
                <span className={`${detailsStyles.scheduledChevron} ${isScheduledPanelExpanded ? detailsStyles.scheduledChevronOpen : ''}`}>▾</span>
              </button>
              {isSelectedConversationExternal && (
                <button
                  type="button"
                  role="switch"
                  aria-checked={!selectedConv.isAiAssistantMuted}
                  onClick={onToggleAiAssistant}
                  disabled={toggleAiAssistantBusy}
                  aria-busy={toggleAiAssistantBusy}
                  className={[
                    detailsStyles.aiSwitch,
                    selectedConv.isAiAssistantMuted ? detailsStyles.aiSwitchOff : detailsStyles.aiSwitchOn,
                    toggleAiAssistantBusy ? detailsStyles.aiSwitchBusy : '',
                  ].filter(Boolean).join(' ')}
                  title={selectedConv.isAiAssistantMuted ? t('enableAiAssistant', { ns: 'conversations' }) : t('muteAiAssistant', { ns: 'conversations' })}
                  aria-label={selectedConv.isAiAssistantMuted ? t('enableAiAssistant', { ns: 'conversations' }) : t('muteAiAssistant', { ns: 'conversations' })}
                >
                  <span className={detailsStyles.aiSwitchTrack}>
                    <span className={detailsStyles.aiSwitchThumb}>
                      {toggleAiAssistantBusy ? <span className={detailsStyles.inlineSpinner} aria-hidden="true" /> : null}
                      {t('aiSwitch', { ns: 'conversations', defaultValue: 'AI' })}
                    </span>
                  </span>
                </button>
              )}
              {selectedConv ? (
                <button
                  type="button"
                  className={`${detailsStyles.filesToggleBtn} ${isAiSettingsPanelOpen ? detailsStyles.filesToggleBtnActive : ''}`}
                  title={t('configureAiAssistant', { ns: 'conversations', defaultValue: 'Configure AI assistant' })}
                  onClick={() => setIsAiSettingsPanelOpen(current => !current)}
                  aria-expanded={isAiSettingsPanelOpen}
                >
                  {t('configureAiAssistant', { ns: 'conversations', defaultValue: 'Configure AI' })}
                </button>
              ) : null}
              <button
                onClick={onToggleSound}
                className={detailsStyles.soundToggleBtn}
                title={isSoundEnabled ? t('muteSoundEffects', { ns: 'conversations' }) : t('enableSoundEffects', { ns: 'conversations' })}
              >
                {renderSoundIcon(isSoundEnabled)}
              </button>
              {canViewAiHistorySettings && (
                <button
                  type="button"
                  className={detailsStyles.filesToggleBtn}
                  title={t('aiActivityTitle', { ns: 'conversations', defaultValue: 'AI activity' })}
                  onClick={() => setIsAiActivityPanelOpen(current => !current)}
                  aria-expanded={isAiActivityPanelOpen}
                >
                  <span>{t('aiActivityTitle', { ns: 'conversations', defaultValue: 'AI Activity' })}</span>
                  <span className={detailsStyles.filesCount}>{aiActivityItems.length}</span>
                  <span className={`${detailsStyles.scheduledChevron} ${isAiActivityPanelOpen ? detailsStyles.scheduledChevronOpen : ''}`}>▾</span>
                </button>
              )}
              </div>
            </div>
            {isWhatsAppGroup && selectedConv ? (
              <WhatsAppAppGroupPanel
                conversationId={selectedConv.id}
                title={selectedConv.title}
                pictureUrl={selectedConv.whatsAppGroupPictureUrl}
                onLeft={onBackToList}
              />
            ) : null}
            {selectedConv ? (
              <ConversationAiSettingsPanel
                conversationId={selectedConv.id}
                canManage={canManageConversations}
                expanded={isAiSettingsPanelOpen}
                onClose={() => setIsAiSettingsPanelOpen(false)}
              />
            ) : null}
            {showFilesPanel && (
              <div className={detailsStyles.filesPopover} role="region" aria-label={t('files', { ns: 'conversations' })}>
                <div className={detailsStyles.utilityPanelHeader}>
                  <div className={detailsStyles.utilityPanelTitle}>
                    {renderFilesPanelIcon(detailsStyles.utilityPanelTitleIcon)}
                    {t('files', { ns: 'conversations' })}
                    <span className={detailsStyles.filesCount}>{conversationFiles.length}</span>
                  </div>
                  <button
                    type="button"
                    className={detailsStyles.utilityPanelCloseBtn}
                    onClick={() => setShowFilesPanel(false)}
                    aria-label={t('closeFilesPanel', { ns: 'conversations' })}
                  >
                    {renderPanelCloseIcon(detailsStyles.utilityPanelCloseIcon)}
                    <span>{t('close', { ns: 'common' })}</span>
                  </button>
                </div>
                <div className={detailsStyles.utilityPanelBody}>
                <input
                  type="text"
                  value={filesSearchTerm}
                  onChange={(event) => setFilesSearchTerm(event.target.value)}
                  className={detailsStyles.filesSearchInput}
                  placeholder={t('searchFiles', { ns: 'conversations' })}
                  aria-label={t('searchFilesAria', { ns: 'conversations' })}
                />
                {groupedConversationFiles.length === 0 ? (
                  <div className={detailsStyles.filesEmpty}>{t('noFilesShared', { ns: 'conversations' })}</div>
                ) : (
                  groupedConversationFiles.map(group => (
                    <div key={group.key} className={detailsStyles.filesGroup}>
                      <div className={detailsStyles.filesGroupTitle}>{group.title}</div>
                      {group.items.map(file => (
                        <a
                          key={file.id}
                          className={detailsStyles.filesItem}
                          href={file.url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() => setShowFilesPanel(false)}
                        >
                          <span className={detailsStyles.filesItemHeader}>
                            <span
                              className={`${detailsStyles.filesItemTypeIcon} ${
                                file.typeKey === 'image'
                                  ? detailsStyles.filesItemTypeIconImage
                                  : file.typeKey === 'video'
                                    ? detailsStyles.filesItemTypeIconVideo
                                    : file.typeKey === 'audio'
                                      ? detailsStyles.filesItemTypeIconAudio
                                      : detailsStyles.filesItemTypeIconDocument
                              }`}
                              aria-hidden="true"
                            >
                              {file.typeKey === 'image'
                                ? '🖼'
                                : file.typeKey === 'video'
                                  ? '🎬'
                                  : file.typeKey === 'audio'
                                    ? '🎵'
                                    : '📄'}
                            </span>
                            <span className={detailsStyles.filesItemName}>{file.name}</span>
                          </span>
                          <span className={detailsStyles.filesItemMeta}>
                            {file.typeLabel} • {file.senderName} • {formatDateTime(file.timestamp)}
                          </span>
                        </a>
                      ))}
                    </div>
                  ))
                )}
                </div>
              </div>
            )}
            <div className={detailsStyles.divider} />
          </div>
          {canAddParticipants && isAddParticipantsOpen && (
            <div className={detailsStyles.modalOverlay} role="dialog" aria-modal="true" aria-label={t('addParticipantsDialog', { ns: 'conversations' })}>
              <div className={detailsStyles.modalCard}>
                <div className={detailsStyles.modalHeader}>
                  <div className={detailsStyles.modalTitle}>{t('addParticipantsDialog', { ns: 'conversations' })}</div>
                  <button
                    type="button"
                    className={detailsStyles.modalCloseBtn}
                    onClick={() => setIsAddParticipantsOpen(false)}
                    disabled={addingParticipants}
                  >
                    {t('close', { ns: 'common' })}
                  </button>
                </div>

                {participantOptionsLoading ? (
                  <div className={detailsStyles.modalHint}>{t('loadingParticipantOptions', { ns: 'conversations' })}</div>
                ) : participantOptionsError ? (
                  <div className={detailsStyles.modalError}>{participantOptionsError}</div>
                ) : availableParticipantOptions.length === 0 ? (
                  <div className={detailsStyles.modalHint}>{t('everyoneAlreadyInConversation', { ns: 'conversations' })}</div>
                ) : (
                  <>
                    <div className={detailsStyles.modalHint}>
                      <input
                        ref={participantSearchInputRef}
                        type="text"
                        value={participantSearchTerm}
                        onChange={event => setParticipantSearchTerm(event.target.value)}
                        className={detailsStyles.filesSearchInput}
                        placeholder={t('searchParticipants', { ns: 'conversations' })}
                        aria-label={t('searchParticipantsAria', { ns: 'conversations' })}
                      />
                    </div>
                    {filteredParticipantOptions.length === 0 ? (
                      <div className={detailsStyles.modalHint}>{t('noParticipantsMatchSearch', { ns: 'conversations' })}</div>
                    ) : (
                      <div className={detailsStyles.modalList}>
                        {filteredParticipantOptions.map(option => {
                      const key = `${option.type}:${option.id}`;
                      const checked = selectedParticipantKeys.has(key);
                      return (
                        <label key={key} className={detailsStyles.modalRow}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSelectedParticipant(key)}
                            disabled={addingParticipants}
                          />
                          <span className={detailsStyles.modalRowMain}>
                            {renderParticipantIdentityAvatar(option, detailsStyles.modalAvatar)}
                            <span className={detailsStyles.modalRowText}>
                              <span className={detailsStyles.modalRowName}>{option.name}</span>
                              <span className={detailsStyles.modalRowMeta}>{option.type}</span>
                            </span>
                          </span>
                        </label>
                      );
                        })}
                      </div>
                    )}
                  </>
                )}

                {addParticipantsError && <div className={detailsStyles.modalError}>{addParticipantsError}</div>}

                <div className={detailsStyles.modalActions}>
                  <button
                    type="button"
                    className={detailsStyles.modalPrimaryBtn}
                    onClick={() => void submitAddParticipants()}
                    disabled={addingParticipants || selectedParticipantKeys.size === 0 || participantOptionsLoading || Boolean(participantOptionsError)}
                  >
                    {addingParticipants ? t('adding', { ns: 'conversations' }) : `${t('add', { ns: 'common' })} ${selectedParticipantKeys.size || ''}`.trim()}
                  </button>
                  <button
                    type="button"
                    className={detailsStyles.modalSecondaryBtn}
                    onClick={() => setSelectedParticipantKeys(new Set())}
                    disabled={addingParticipants || selectedParticipantKeys.size === 0}
                  >
                    {t('clear', { ns: 'common' })}
                  </button>
                </div>
              </div>
            </div>
          )}
          {canManageTasks && isCreateTaskModalOpen && selectedConv && (
            <ConversationCreateTaskModal
              conversationId={selectedConv.id}
              sourceMessage={createTaskMessage}
              conversationDisplayName={getConversationDisplayName(selectedConv)}
              getParticipantDisplayName={getParticipantDisplayName}
              initialTitle={createTaskMessage
                ? buildMessageTaskTitle(createTaskMessage, getParticipantDisplayName(createTaskMessage.sender), t('noContent', { ns: 'conversations' }))
                : t('defaultConversationTaskTitle', { ns: 'conversations' })}
              initialDescription={createTaskMessage?.content?.trim() ?? ''}
              onClose={closeCreateTaskModal}
              onCreated={() => void handleTaskCreated()}
            />
          )}
          <div
            className={[
              detailsStyles.contentArea,
              isAiActivityPanelOpen ? detailsStyles.contentAreaAiActivity : '',
            ].filter(Boolean).join(' ')}
          >
            <div className={detailsStyles.messagesPane} ref={messagesPaneRef}>
              {selectedConv.hasMoreMessages ? (
                <div className={detailsStyles.messagesHistoryActions}>
                  <button
                    type="button"
                    className={detailsStyles.loadOlderMessagesButton}
                    onClick={() => void onLoadOlderMessages()}
                    disabled={loadingOlderMessages}
                  >
                    {loadingOlderMessages
                      ? t('loadingOlderMessages', { ns: 'conversations' })
                      : t('loadOlderMessages', { ns: 'conversations' })}
                  </button>
                </div>
              ) : null}
              {displayMessages.length === 0 ? (
                <div className={detailsStyles.emptyState}>{t('noMessagesYet', { ns: 'conversations' })}</div>
              ) : (
                (() => {
                  const groupedMessages: { date: string; messages: typeof displayMessages }[] = [];
                  displayMessages.forEach(message => {
                    const messageDate = formatLongDateLabel(message.timestamp);
                    const lastGroup = groupedMessages[groupedMessages.length - 1];
                    if (lastGroup && lastGroup.date === messageDate) {
                      lastGroup.messages.push(message);
                    } else {
                      groupedMessages.push({ date: messageDate, messages: [message] });
                    }
                  });

                  return groupedMessages.map(group => (
                    <React.Fragment key={group.date}>
                      <div className={detailsStyles.dateSeparator}>
                        <span className={detailsStyles.dateLabel}>{group.date}</span>
                      </div>
                      {group.messages.map(message => {
                        const isSystemMessage = message.messageType === 'system' || message.sender.type === 'system';
                        const isExternalReplyAudit = isExternalReplyAuditMessage(message);
                        const isMe = meSender && message.sender.type === meSender.type && message.sender.id === meSender.id;
                        const isAiAssistantMessage = message.sender.type === 'aiAssistant';
                        const isOutgoingMessage = isOutgoingConversationMessage(message, meSender, isSelectedConversationExternal);
                        const senderName = getParticipantDisplayName(message.sender);
                        const quotedSenderName = message.quotedMessage ? getParticipantDisplayName(message.quotedMessage.sender) : null;
                        const quotedSnippet = message.quotedMessage ? truncateMessageSnippet(getQuotedMessageSnippet(message.quotedMessage), 120) : null;
                        const isPendingMessage = isMe && isOptimisticMessageId(message.id);
                        const statusLabel = isPendingMessage
                          ? t('sendingMessage', { ns: 'conversations' })
                          : message.status === 'sent'
                            ? t('sent', { ns: 'conversations' })
                            : message.status === 'delivered'
                              ? t('delivered', { ns: 'conversations' })
                              : t('failed', { ns: 'conversations' });
                        const deliveryTooltip = isMe && (
                          (selectedConv.containsWhatsAppMessages && (message.whatsAppStatusValue || message.whatsAppMessageId || message.whatsAppBizOpaqueCallbackData))
                          || (message.source === 'instagram' || message.instagramMessageId || message.instagramStatusValue || message.instagramRecipientId)
                          || (message.source === 'messenger' || message.messengerMessageId || message.messengerStatusValue || message.messengerRecipientId)
                        )
                          ? buildChannelDeliveryTooltip(message, statusLabel, {
                              whatsAppStatus: t('whatsAppDeliveryStatus', { ns: 'conversations' }),
                              whatsAppMessageId: t('whatsAppMessageId', { ns: 'conversations' }),
                              whatsAppCallbackRef: t('whatsAppCallbackRef', { ns: 'conversations' }),
                              instagramStatus: t('instagramDeliveryStatus', { ns: 'conversations' }),
                              instagramMessageId: t('instagramMessageId', { ns: 'conversations' }),
                              instagramRecipientId: t('instagramRecipientId', { ns: 'conversations' }),
                              instagramPrivateReplyCommentId: t('instagramPrivateReplyCommentId', { ns: 'conversations' }),
                              messengerStatus: t('messengerDeliveryStatus', { ns: 'conversations' }),
                              messengerMessageId: t('messengerMessageId', { ns: 'conversations' }),
                              messengerRecipientId: t('messengerRecipientId', { ns: 'conversations' }),
                            })
                          : statusLabel;
                        const readByOthers = message.readBy.filter(reader => !(reader.type === message.sender.type && reader.id === message.sender.id));
                        const readCount = readByOthers.length;
                        const senderDestination = resolveParticipantLink(message.sender);
                        const quickEmojis = ['👍', '❤️', '😂', '🎉', '👏'];
                        const translatedText = translatedMessagesById[message.id]?.trim() ?? '';
                        const hasTranslatedText = translatedText.length > 0;
                        const isTranslatedVisible = translatedMessageVisibilityById[message.id] ?? true;
                        const isTranslating = translatingMessageId === message.id;
                        const canTranslateTextMessage = message.messageType === 'text' && Boolean(message.content?.trim());
                        const instagramCommentLabelKey = getInstagramCommentSurfaceLabelKey(message);
                        const isMessengerComment = isMessengerCommentMessage(message);
                        const isActiveInstagramCommentTarget = Boolean(
                          activeInstagramCommentTargetId
                          && message.id === activeInstagramCommentTargetId
                          && isInstagramCommentMessage(message),
                        );
                        const isActiveMessengerCommentTarget = Boolean(
                          activeMessengerCommentTargetId
                          && message.id === activeMessengerCommentTargetId
                          && isMessengerComment,
                        );
                        const showInstagramCommentReplyAction = Boolean(
                          selectedConv
                          && !isOutgoingMessage
                          && isInstagramCommentReplyTarget(message),
                        );
                        const showMessengerCommentReplyAction = Boolean(
                          selectedConv
                          && !isOutgoingMessage
                          && isMessengerCommentReplyTarget(message),
                        );
                        const instagramCommentReply = selectedConv
                          && isInstagramCommentMessage(message)
                          && !isOutgoingMessage
                          ? findInstagramReplyMessageForComment(selectedConv.messages, message)
                          : null;
                        const messengerCommentReply = selectedConv
                          && isMessengerComment
                          && !isOutgoingMessage
                          ? findMessengerReplyMessageForComment(selectedConv.messages, message)
                          : null;

                        return (
                          <div key={message.id} id={`conversation-msg-${message.id}`} className={`${detailsStyles.messageRow} ${isSystemMessage ? detailsStyles.systemMessageRow : ''} ${highlightedMessageId === message.id ? detailsStyles.messageRowHighlighted : ''}`} style={{ alignSelf: isSystemMessage ? 'center' : isOutgoingMessage ? 'flex-end' : 'flex-start' }}>
                            {isSystemMessage ? (
                              <div className={`${detailsStyles.systemMessageCard} ${isExternalReplyAudit ? detailsStyles.systemMessageCardExternalReply : ''}`}>
                                <div className={`${detailsStyles.systemMessageLabel} ${isExternalReplyAudit ? detailsStyles.systemMessageLabelExternalReply : ''}`}>
                                  {isExternalReplyAudit ? t('externalReply', { ns: 'conversations' }) : t('system', { ns: 'conversations' })}
                                </div>
                                <button
                                  type="button"
                                  className={detailsStyles.systemQuoteBtn}
                                  onClick={() => onQuoteMessage(message)}
                                  title={t('quoteMessage', { ns: 'conversations' })}
                                >
                                  ↩
                                </button>
                                <div className={detailsStyles.systemMessageBody}>{renderMessageContent(message)}</div>
                                <div className={detailsStyles.systemMessageTime}>{formatTime(message.timestamp)}</div>
                              </div>
                            ) : (
                              <>
                                <div className={`${detailsStyles.messageLayout} ${isOutgoingMessage ? detailsStyles.me : ''}`}>
                                  <div className={detailsStyles.messageAuthorRail}>
                                    {senderDestination ? (
                                      <button
                                        type="button"
                                        className={`${detailsStyles.senderAvatarButton} ${detailsStyles.messageSideAvatarButton}`}
                                        onClick={() => onParticipantClick(message.sender)}
                                        aria-label={t('openParticipant', { ns: 'conversations', name: senderName })}
                                        title={t('openParticipant', { ns: 'conversations', name: senderName })}
                                      >
                                        {renderParticipantIdentityAvatar(message.sender, `${detailsStyles.senderAvatar} ${detailsStyles.messageSideAvatar}`)}
                                      </button>
                                    ) : (
                                      <span className={detailsStyles.messageSideAvatarWrap}>
                                        {renderParticipantIdentityAvatar(message.sender, `${detailsStyles.senderAvatar} ${detailsStyles.messageSideAvatar}`)}
                                      </span>
                                    )}
                                  </div>
                                  <div className={detailsStyles.messageContentColumn}>
                                    <div className={`${detailsStyles.messageHeaderBadges} ${isOutgoingMessage ? detailsStyles.me : ''}`}>
                                      <span
                                        className={`${detailsStyles.messageSourceBadge} ${message.source?.toLowerCase() === 'messenger' ? detailsStyles.messageSourceBadgeMessenger : ''}`}
                                      >
                                        {getMessageSourceContent(message.source)}
                                      </span>
                                      {instagramCommentLabelKey ? (
                                        <span className={`${detailsStyles.messageSourceBadge} ${detailsStyles.messageSourceBadgeInstagramComment}`}>
                                          {t(instagramCommentLabelKey, { ns: 'conversations' })}
                                        </span>
                                      ) : null}
                                      {isMessengerComment ? (
                                        <span className={`${detailsStyles.messageSourceBadge} ${detailsStyles.messageSourceBadgeInstagramComment}`}>
                                          {t('messengerCommentBadge', { ns: 'conversations' })}
                                        </span>
                                      ) : null}
                                      {message.instagramPrivateReplyCommentId ? (
                                        <span className={`${detailsStyles.messageSourceBadge} ${detailsStyles.messageSourceBadgeInstagramComment}`}>
                                          {t('instagramPrivateReplySentBadge', { ns: 'conversations' })}
                                        </span>
                                      ) : null}
                                      {message.instagramPublicReplyCommentId ? (
                                        <span className={`${detailsStyles.messageSourceBadge} ${detailsStyles.messageSourceBadgeInstagramComment}`}>
                                          {t('instagramPublicReplySentBadge', { ns: 'conversations' })}
                                        </span>
                                      ) : null}
                                      {message.instagramCommentHidden ? (
                                        <span className={`${detailsStyles.messageSourceBadge} ${detailsStyles.messageSourceBadgeInstagramComment}`}>
                                          {t('instagramCommentHiddenBadge', { ns: 'conversations' })}
                                        </span>
                                      ) : null}
                                      {message.instagramCommentDeleted ? (
                                        <span className={`${detailsStyles.messageSourceBadge} ${detailsStyles.messageSourceBadgeInstagramComment}`}>
                                          {t('instagramCommentDeletedBadge', { ns: 'conversations' })}
                                        </span>
                                      ) : null}
                                      {message.messengerPrivateReplyCommentId ? (
                                        <span className={`${detailsStyles.messageSourceBadge} ${detailsStyles.messageSourceBadgeInstagramComment}`}>
                                          {t('messengerPrivateReplySentBadge', { ns: 'conversations' })}
                                        </span>
                                      ) : null}
                                      {message.messengerPublicReplyCommentId ? (
                                        <span className={`${detailsStyles.messageSourceBadge} ${detailsStyles.messageSourceBadgeInstagramComment}`}>
                                          {t('messengerPublicReplySentBadge', { ns: 'conversations' })}
                                        </span>
                                      ) : null}
                                      {message.messengerCommentHidden ? (
                                        <span className={`${detailsStyles.messageSourceBadge} ${detailsStyles.messageSourceBadgeInstagramComment}`}>
                                          {t('messengerCommentHiddenBadge', { ns: 'conversations' })}
                                        </span>
                                      ) : null}
                                      {message.messengerCommentDeleted ? (
                                        <span className={`${detailsStyles.messageSourceBadge} ${detailsStyles.messageSourceBadgeInstagramComment}`}>
                                          {t('messengerCommentDeletedBadge', { ns: 'conversations' })}
                                        </span>
                                      ) : null}
                                      {message.messageType !== 'text' && (
                                        <span className={detailsStyles.messageSourceBadge}>{getMessageTypeLabel(message.messageType)}</span>
                                      )}
                                    </div>
                                    <div className={`${detailsStyles.messageContainer} ${isOutgoingMessage ? detailsStyles.me : ''}`}>
                                  <div className={detailsStyles.messageActions} aria-hidden="true">
                                      {canManageTasks && (
                                        <button
                                          type="button"
                                          className={detailsStyles.taskMessageBtn}
                                          onClick={() => openCreateTaskModal(message)}
                                          title={t('createTaskFromMessage', { ns: 'conversations' })}
                                        >
                                          T
                                        </button>
                                      )}
                                    <button
                                      type="button"
                                      className={detailsStyles.quoteBtn}
                                      onClick={() => onQuoteMessage(message)}
                                      title={t('quoteMessage', { ns: 'conversations' })}
                                    >
                                      ↩
                                    </button>
                                    {onDeleteMessage
                                      && !isSystemMessage
                                      && meSender
                                      && message.sender.type === meSender.type
                                      && idsEqual(message.sender.id, meSender.id)
                                      && !isInstagramCommentMessage(message)
                                      && !isMessengerComment ? (
                                      <button
                                        type="button"
                                        className={`${detailsStyles.moderationBtn} ${detailsStyles.moderationBtnDanger}`}
                                        onClick={() => {
                                          if (window.confirm(t('deleteMessageConfirm', { ns: 'conversations' }))) {
                                            void onDeleteMessage(message.id);
                                          }
                                        }}
                                        title={t('deleteMessage', { ns: 'conversations' })}
                                      >
                                        {t('deleteMessage', { ns: 'conversations' })}
                                      </button>
                                    ) : null}
                                    {!isOutgoingMessage && isInstagramCommentMessage(message) && !message.instagramCommentDeleted ? (
                                      <>
                                        {message.instagramCommentHidden ? (
                                          <button
                                            type="button"
                                            className={detailsStyles.moderationBtn}
                                            onClick={() => void onModerateInstagramComment(message.id, 'unhide')}
                                            title={t('instagramCommentUnhideAction', { ns: 'conversations' })}
                                          >
                                            {t('instagramCommentUnhideAction', { ns: 'conversations' })}
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            className={detailsStyles.moderationBtn}
                                            onClick={() => void onModerateInstagramComment(message.id, 'hide')}
                                            title={t('instagramCommentHideAction', { ns: 'conversations' })}
                                          >
                                            {t('instagramCommentHideAction', { ns: 'conversations' })}
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          className={`${detailsStyles.moderationBtn} ${detailsStyles.moderationBtnDanger}`}
                                          onClick={() => {
                                            if (window.confirm(t('instagramCommentDeleteConfirm', { ns: 'conversations' }))) {
                                              void onModerateInstagramComment(message.id, 'delete');
                                            }
                                          }}
                                          title={t('instagramCommentDeleteAction', { ns: 'conversations' })}
                                        >
                                          {t('instagramCommentDeleteAction', { ns: 'conversations' })}
                                        </button>
                                      </>
                                    ) : null}
                                    {!isOutgoingMessage && isMessengerComment && !message.messengerCommentDeleted ? (
                                      <>
                                        {message.messengerCommentHidden ? (
                                          <button
                                            type="button"
                                            className={detailsStyles.moderationBtn}
                                            onClick={() => void onModerateMessengerComment(message.id, 'unhide')}
                                            title={t('messengerCommentUnhideAction', { ns: 'conversations' })}
                                          >
                                            {t('messengerCommentUnhideAction', { ns: 'conversations' })}
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            className={detailsStyles.moderationBtn}
                                            onClick={() => void onModerateMessengerComment(message.id, 'hide')}
                                            title={t('messengerCommentHideAction', { ns: 'conversations' })}
                                          >
                                            {t('messengerCommentHideAction', { ns: 'conversations' })}
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          className={`${detailsStyles.moderationBtn} ${detailsStyles.moderationBtnDanger}`}
                                          onClick={() => {
                                            if (window.confirm(t('messengerCommentDeleteConfirm', { ns: 'conversations' }))) {
                                              void onModerateMessengerComment(message.id, 'delete');
                                            }
                                          }}
                                          title={t('messengerCommentDeleteAction', { ns: 'conversations' })}
                                        >
                                          {t('messengerCommentDeleteAction', { ns: 'conversations' })}
                                        </button>
                                      </>
                                    ) : null}
                                    <button
                                      className={detailsStyles.reactionBtn}
                                      onClick={() => setShowReactionPicker(showReactionPicker === message.id ? null : message.id)}
                                      title={t('addReaction', { ns: 'conversations' })}
                                    >
                                      😊
                                    </button>
                                    {canTranslateTextMessage ? (
                                      <button
                                        type="button"
                                        className={detailsStyles.translateBtn}
                                        onClick={() => {
                                          if (hasTranslatedText) {
                                            onToggleTranslatedMessageVisibility(message.id);
                                            return;
                                          }

                                          void onTranslateMessage(message);
                                        }}
                                        title={hasTranslatedText
                                          ? (isTranslatedVisible ? t('hideTranslatedMessage', { ns: 'conversations' }) : t('showTranslatedMessage', { ns: 'conversations' }))
                                          : (isTranslating ? t('translatingMessage', { ns: 'conversations' }) : t('translateMessage', { ns: 'conversations' }))}
                                        aria-label={hasTranslatedText
                                          ? (isTranslatedVisible ? t('hideTranslatedMessage', { ns: 'conversations' }) : t('showTranslatedMessage', { ns: 'conversations' }))
                                          : (isTranslating ? t('translatingMessage', { ns: 'conversations' }) : t('translateMessage', { ns: 'conversations' }))}
                                        disabled={isTranslating}
                                      >
                                        {hasTranslatedText ? (
                                          isTranslatedVisible ? (
                                            <svg className={detailsStyles.translateBtnIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                              <path d="M2 2l20 20M10.7 6.3A9 9 0 0 1 12 6c5 0 9 4.5 10 6-.5.8-1.7 2.4-3.4 3.8M6.1 8.1C4.2 9.5 2.8 11.4 2 12c.6.9 2.4 3.5 5 5M9.9 14.1a3 3 0 0 0 4.2 0M14.1 9.9a3 3 0 0 0-4.2 0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          ) : (
                                            <svg className={detailsStyles.translateBtnIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                              <path d="M2 12c1-1.5 5-6 10-6s9 4.5 10 6c-1 1.5-5 6-10 6s-9-4.5-10-6z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                              <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
                                            </svg>
                                          )
                                        ) : (
                                          <svg className={detailsStyles.translateBtnIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                            <path d="M4 5h8M8 5c0 5-2 8-4 10m4-10c0 5 2 8 4 10M13 19l3-8 3 8m-5-3h4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                        )}
                                      </button>
                                    ) : null}
                                    {showReactionPicker === message.id && (
                                      <div className={detailsStyles.reactionPicker}>
                                        {quickEmojis.map(emoji => {
                                          const reactionKey = `${selectedConv.id}:${message.id}:${emoji}`;
                                          const isPendingReaction = pendingReactionKeys.has(reactionKey);

                                          return (
                                            <button
                                              key={emoji}
                                              className={`${detailsStyles.emojiBtn} ${isPendingReaction ? detailsStyles.emojiBtnPending : ''}`}
                                              onClick={() => onReaction(message.id, emoji)}
                                              aria-busy={isPendingReaction}
                                            >
                                              {emoji}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                  <div className={detailsStyles.messageContainerInner}>
                                    <div className={`${detailsStyles.bubble} ${isOutgoingMessage ? detailsStyles.me : ''} ${isAiAssistantMessage ? detailsStyles.bubbleAiAssistant : ''}`}>
                                      {(isWhatsAppGroup || !isOutgoingMessage) ? (
                                        <div className={`${detailsStyles.bubbleAuthorName} ${isOutgoingMessage ? detailsStyles.me : ''}`}>{senderName}</div>
                                      ) : null}
                                      {message.quotedMessage && quotedSenderName && quotedSnippet ? (
                                        <button
                                          type="button"
                                          className={`${detailsStyles.quotedMessagePreview} ${detailsStyles.quotedMessagePreviewButton}`}
                                          onClick={() => onJumpToMessage(message.quotedMessageId)}
                                        >
                                          <span className={detailsStyles.quotedMessageLabel}>{quotedSenderName}</span>
                                          <span className={detailsStyles.quotedMessageText}>{quotedSnippet}</span>
                                        </button>
                                      ) : null}
                                      {message.contentPreview ? (
                                        <InstagramContentPreviewCard preview={message.contentPreview} />
                                      ) : null}
                                      {renderMessageContent(message)}
                                      <div className={detailsStyles.bubbleMeta}>
                                        <span>{formatTime(message.timestamp)}</span>
                                        {isMe && (
                                          <>
                                            <span className={`${detailsStyles.statusIcon} ${isPendingMessage ? detailsStyles.pending : detailsStyles[message.status]}`} title={deliveryTooltip}>
                                              {isPendingMessage ? renderPendingStatusIcon() : renderMessageStatusIcon(message.status)}
                                            </span>
                                            {readCount > 0 && (
                                              <span className={detailsStyles.readReceipt} title={t('readByCount', { ns: 'conversations', count: readCount })}>
                                                👁 {readCount}
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    {Object.keys(message.reactions).length > 0 && (
                                      <div className={`${detailsStyles.reactionsDisplay} ${isOutgoingMessage ? detailsStyles.me : ''}`}>
                                        {Object.entries(message.reactions).map(([emoji, reactors]) => {
                                          const reactionKey = `${selectedConv.id}:${message.id}:${emoji}`;
                                          const isPendingReaction = pendingReactionKeys.has(reactionKey);
                                          const iReacted = reactors.some(reactor => meSender && reactor.type === meSender.type && reactor.id === meSender.id);
                                          const reactorNames = reactors.map(reactor =>
                                            meSender && reactor.type === meSender.type && reactor.id === meSender.id
                                              ? t('you', { ns: 'common', defaultValue: 'You' })
                                              : getParticipantDisplayName(reactor)
                                          );
                                          return (
                                              <div key={emoji} className={detailsStyles.reactionBadgeWrapper}>
                                                <button
                                                  className={`${detailsStyles.reactionBadge} ${iReacted ? detailsStyles.myReaction : ''} ${isPendingReaction ? detailsStyles.reactionBadgePending : ''}`}
                                                  onClick={() => onReaction(message.id, emoji)}
                                                  aria-busy={isPendingReaction}
                                                >
                                                  {emoji} {reactors.length > 1 ? reactors.length : ''}
                                                </button>
                                                <div className={detailsStyles.reactionTooltip}>
                                                  <span className={detailsStyles.reactionTooltipEmoji}>{emoji}</span>
                                                  <span className={detailsStyles.reactionTooltipNames}>{reactorNames.join(', ')}</span>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    {showInstagramCommentReplyAction || showMessengerCommentReplyAction || instagramCommentReply || messengerCommentReply ? (
                                      <div className={detailsStyles.commentReplyActions}>
                                        {showInstagramCommentReplyAction ? (
                                          <button
                                            type="button"
                                            className={`${detailsStyles.commentReplyAction} ${isActiveInstagramCommentTarget ? detailsStyles.commentReplyActionActive : ''}`}
                                            onClick={() => onSelectInstagramCommentTarget(message)}
                                          >
                                            {t('instagramReplyToCommentAction', { ns: 'conversations' })}
                                          </button>
                                        ) : null}
                                        {showMessengerCommentReplyAction ? (
                                          <button
                                            type="button"
                                            className={`${detailsStyles.commentReplyAction} ${isActiveMessengerCommentTarget ? detailsStyles.commentReplyActionActive : ''}`}
                                            onClick={() => onSelectMessengerCommentTarget(message)}
                                          >
                                            {t('messengerReplyToCommentAction', { ns: 'conversations' })}
                                          </button>
                                        ) : null}
                                        {instagramCommentReply ? (
                                          <button
                                            type="button"
                                            className={detailsStyles.commentRepliedAction}
                                            onClick={() => void onJumpToMessage(instagramCommentReply.message.id)}
                                          >
                                            {t('instagramCommentRepliedLabel', { ns: 'conversations' })}
                                            {' · '}
                                            {t(getInstagramReplyModeLabelKey(instagramCommentReply.replyMode), { ns: 'conversations' })}
                                          </button>
                                        ) : null}
                                        {messengerCommentReply ? (
                                          <button
                                            type="button"
                                            className={detailsStyles.commentRepliedAction}
                                            onClick={() => void onJumpToMessage(messengerCommentReply.message.id)}
                                          >
                                            {t('messengerCommentRepliedLabel', { ns: 'conversations' })}
                                            {' · '}
                                            {t(getMessengerReplyModeLabelKey(messengerCommentReply.replyMode), { ns: 'conversations' })}
                                          </button>
                                        ) : null}
                                      </div>
                                    ) : null}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ));
              })()
              )}
            </div>
            {unreadCount > 0 && (
              <div className={detailsStyles.unreadIndicator} onClick={scrollToBottom}>
                <span className={detailsStyles.unreadIcon}>↓</span>
                {t('newMessages', { ns: 'conversations', count: unreadCount })}
              </div>
            )}
            {(aiDraft || aiDraftError) && (
              <div className={detailsStyles.aiDraftCard}>
                <div className={detailsStyles.aiDraftHeader}>
                  <span className={detailsStyles.aiDraftTitle}>{t('aiReplyDraft', { ns: 'conversations' })}</span>
                  {aiDraft && (
                    <div className={detailsStyles.aiDraftActions}>
                      <button
                        type="button"
                        className={detailsStyles.aiDraftApplyBtn}
                        onClick={onUseAiDraft}
                      >
                        {t('useDraft', { ns: 'conversations' })}
                      </button>
                      <button
                        type="button"
                        className={detailsStyles.aiDraftDismissBtn}
                        onClick={onDismissAiDraft}
                      >
                        {t('close', { ns: 'common' })}
                      </button>
                    </div>
                  )}
                </div>
                <div className={detailsStyles.aiDraftBody}>
                  {aiDraftError || aiDraft}
                </div>
              </div>
            )}
            {(aiSummary || aiSummaryError) && (
              <div className={detailsStyles.aiDraftCard}>
                <div className={detailsStyles.aiDraftHeader}>
                  <span className={detailsStyles.aiDraftTitle}>{t('aiConversationSummary', { ns: 'conversations' })}</span>
                  <div className={detailsStyles.aiDraftActions}>
                    <button
                      type="button"
                      className={detailsStyles.aiDraftApplyBtn}
                      onClick={onUseAiSummary}
                      disabled={!aiSummary}
                    >
                      {t('insertSummary', { ns: 'conversations' })}
                    </button>
                    <button
                      type="button"
                      className={detailsStyles.aiDraftBtn}
                      onClick={onCopyAiSummary}
                      disabled={!aiSummary || copyingAiSummary}
                    >
                      {copyingAiSummary ? t('copyingSummary', { ns: 'conversations' }) : t('copySummary', { ns: 'conversations' })}
                    </button>
                    <button
                      type="button"
                      className={detailsStyles.aiDraftApplyBtn}
                      onClick={onSaveAiSummaryAsNote}
                      disabled={!aiSummary || savingAiSummaryAsNote}
                    >
                      {savingAiSummaryAsNote ? t('saving', { ns: 'common' }) : t('saveSummaryAsNote', { ns: 'conversations' })}
                    </button>
                    <button
                      type="button"
                      className={detailsStyles.aiDraftDismissBtn}
                      onClick={onDismissAiSummary}
                    >
                      {t('close', { ns: 'common' })}
                    </button>
                  </div>
                </div>
                <div className={detailsStyles.aiDraftBody}>
                  {aiSummaryError || aiSummary}
                </div>
              </div>
            )}
            {canViewAiHistorySettings && selectedConv && isAiActivityPanelOpen && (
              <div
                className={`${detailsStyles.aiActivityPanel} ${detailsStyles.aiActivityPanelOpen}`}
                aria-hidden={false}
              >
                <div className={detailsStyles.aiActivityCard}>
                <div className={detailsStyles.aiActivityHeader}>
                  <div>
                    <div className={detailsStyles.aiActivityTitle}>{t('aiActivityTitle', { ns: 'conversations', defaultValue: 'AI activity' })}</div>
                    <div className={detailsStyles.aiActivitySubtitle}>{t('aiActivitySubtitle', { ns: 'conversations', defaultValue: 'Admin-only plugin and tool execution history for this conversation.' })}</div>
                    {aiActivityRunIdFilter && (
                      <div className={detailsStyles.aiActivityRunFilter}>
                        <span>{t('currentRunFilter', { ns: 'conversations', defaultValue: 'Current run linked' })}</span>
                        <button
                          type="button"
                          className={detailsStyles.noteCardSecondaryBtn}
                          onClick={() => setAiActivityRunIdFilter(null)}
                        >
                          {t('clear', { ns: 'common' })}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className={detailsStyles.aiActivityHeaderActions}>
                    <Link
                      to={`/ai/activity?conversationId=${encodeURIComponent(selectedConv.id)}${aiActivityRunIdFilter ? `&runId=${encodeURIComponent(aiActivityRunIdFilter)}` : ''}`}
                      className={detailsStyles.noteCardSecondaryBtn}
                    >
                      {t('viewAllAiActivity', { ns: 'conversations', defaultValue: 'View all activity' })}
                    </Link>
                    <button
                      type="button"
                      className={detailsStyles.noteCardSecondaryBtn}
                      onClick={() => void copyVisibleAiActivity()}
                      disabled={aiActivityLoading || (aiActivityItems.length === 0 && visibleAiRunSummaries.length === 0)}
                    >
                      {t('copyAiActivityExport', { ns: 'conversations', defaultValue: 'Copy JSON' })}
                    </button>
                    <button
                      type="button"
                      className={detailsStyles.noteCardSecondaryBtn}
                      onClick={() => void loadAiActivity(selectedConv.id, false)}
                      disabled={aiActivityLoading}
                    >
                      {aiActivityLoading
                        ? t('loading', { ns: 'common' })
                        : t('refreshAiActivity', { ns: 'conversations', defaultValue: 'Refresh' })}
                    </button>
                    <button
                      type="button"
                      className={detailsStyles.noteCardSecondaryBtn}
                      onClick={() => setIsAiActivityPanelOpen(false)}
                    >
                      {t('close', { ns: 'common' })}
                    </button>
                  </div>
                </div>
                <div className={`${detailsStyles.aiActivitySection} ${detailsStyles.aiActivityMemorySection}`}>
                  <button
                    type="button"
                    className={detailsStyles.aiActivitySectionToggle}
                    onClick={() => setIsAiMemorySectionExpanded(current => !current)}
                    aria-expanded={isAiMemorySectionExpanded}
                  >
                    <span className={detailsStyles.aiActivitySectionTitle}>{t('aiMemoryTitle', { ns: 'conversations', defaultValue: 'AI memory' })}</span>
                    <span className={`${detailsStyles.aiActivitySectionChevron} ${isAiMemorySectionExpanded ? detailsStyles.aiActivitySectionChevronOpen : ''}`}>▾</span>
                  </button>
                  {isAiMemorySectionExpanded && (
                    <div className={detailsStyles.aiActivitySectionBody}>
                      <div className={detailsStyles.aiDraftActions}>
                        <button
                          type="button"
                          className={detailsStyles.aiDraftBtn}
                          onClick={onCopyAiMemory}
                          disabled={!aiMemory || aiMemoryLoading || copyingAiMemory || refreshingAiMemory || clearingAiMemory}
                        >
                          {copyingAiMemory
                            ? t('copyingAiMemory', { ns: 'conversations', defaultValue: 'Copying...' })
                            : t('copyAiMemory', { ns: 'conversations', defaultValue: 'Copy memory' })}
                        </button>
                        <button
                          type="button"
                          className={detailsStyles.aiDraftBtn}
                          onClick={onRefreshAiMemory}
                          disabled={aiMemoryLoading || refreshingAiMemory || clearingAiMemory}
                        >
                          {refreshingAiMemory
                            ? t('refreshingAiMemory', { ns: 'conversations', defaultValue: 'Refreshing...' })
                            : t('refreshAiMemory', { ns: 'conversations', defaultValue: 'Refresh memory' })}
                        </button>
                        <button
                          type="button"
                          className={detailsStyles.aiDraftDismissBtn}
                          onClick={onClearAiMemory}
                          disabled={aiMemoryLoading || refreshingAiMemory || clearingAiMemory || (!aiMemory && !aiMemoryError)}
                        >
                          {clearingAiMemory
                            ? t('clearingAiMemory', { ns: 'conversations', defaultValue: 'Clearing...' })
                            : t('clearAiMemory', { ns: 'conversations', defaultValue: 'Clear memory' })}
                        </button>
                      </div>
                      <div className={`${detailsStyles.aiDraftBody} ${detailsStyles.aiActivityMemoryBody} ${!aiMemory && !aiMemoryError && !aiMemoryLoading ? detailsStyles.aiMemoryBodyEmpty : ''}`}>
                        {aiMemoryLoading
                          ? t('loadingAiMemory', { ns: 'conversations', defaultValue: 'Loading AI memory...' })
                          : aiMemoryError
                            ? aiMemoryError
                            : aiMemory || t('aiMemoryEmpty', { ns: 'conversations', defaultValue: 'No persisted AI memory is stored for this conversation yet.' })}
                      </div>
                    </div>
                  )}
                </div>
                <div className={detailsStyles.aiActivityScrollRegion}>
                <div className={detailsStyles.aiActivityFilters}>
                  <label className={detailsStyles.aiActivityFilterField}>
                    <span>{t('status', { ns: 'common', defaultValue: 'Status' })}</span>
                    <select value={aiActivityStatusFilter} onChange={event => setAiActivityStatusFilter(event.target.value)}>
                      <option value="all">{t('allStatuses', { ns: 'conversations', defaultValue: 'All statuses' })}</option>
                      <option value="Succeeded">{t('succeeded', { ns: 'conversations', defaultValue: 'Succeeded' })}</option>
                      <option value="Failed">{t('failed', { ns: 'conversations' })}</option>
                    </select>
                  </label>
                  <label className={detailsStyles.aiActivityFilterField}>
                    <span>{t('plugin', { ns: 'conversations', defaultValue: 'Plugin' })}</span>
                    <select value={aiActivityPluginFilter} onChange={event => setAiActivityPluginFilter(event.target.value)}>
                      <option value="all">{t('allPlugins', { ns: 'conversations', defaultValue: 'All plugins' })}</option>
                      {aiActivityPluginOptions.map(pluginName => (
                        <option key={pluginName} value={pluginName}>{formatAiActivityPluginLabel(pluginName)}</option>
                      ))}
                    </select>
                  </label>
                  <label className={detailsStyles.aiActivityFilterField}>
                    <span>{t('triggeredBy', { ns: 'conversations', defaultValue: 'Triggered by' })}</span>
                    <select value={aiActivityTriggeredByFilter} onChange={event => setAiActivityTriggeredByFilter(event.target.value)}>
                      <option value="all">{t('allSources', { ns: 'conversations', defaultValue: 'All sources' })}</option>
                      <option value="ai-assistant">{t('aiAssistant', { ns: 'conversations', defaultValue: 'AI assistant' })}</option>
                      <option value="employee">{t('employee', { ns: 'conversations', defaultValue: 'Employee' })}</option>
                      <option value="system">{t('system', { ns: 'conversations' })}</option>
                    </select>
                  </label>
                </div>
                {visibleAiRunSummaries.length > 0 && (
                  <div className={detailsStyles.aiActivitySummaryGrid}>
                    {visibleAiRunSummaries.map(summary => {
                      const isActiveRun = aiActivityRunIdFilter === summary.runId;

                      return (
                        <button
                          key={summary.runId}
                          type="button"
                          className={[
                            detailsStyles.aiActivitySummaryCard,
                            isActiveRun ? detailsStyles.aiActivitySummaryCardActive : '',
                          ].filter(Boolean).join(' ')}
                          onClick={() => setAiActivityRunIdFilter(isActiveRun ? null : summary.runId)}
                        >
                          <div className={detailsStyles.aiActivitySummaryHeader}>
                            <span className={detailsStyles.aiActivitySummaryRun}>
                              {t('aiThinkingRunLabel', {
                                ns: 'conversations',
                                defaultValue: 'Run {{runId}}',
                                runId: formatAiRunLabel(summary.runId),
                              })}
                            </span>
                            <span className={detailsStyles.aiActivitySummaryTime}>{formatDateTime(summary.endedAt)}</span>
                          </div>
                          <div className={detailsStyles.aiActivitySummaryMetrics}>
                            <span className={detailsStyles.aiActivitySummaryMetric}>{summary.operationCount} ops</span>
                            <span className={detailsStyles.aiActivitySummaryMetric}>{summary.completionCount} completions</span>
                            <span className={detailsStyles.aiActivitySummaryMetric}>{formatAiTokenCount(summary.inputTokens) ?? '0'} in</span>
                            <span className={detailsStyles.aiActivitySummaryMetric}>{formatAiTokenCount(summary.outputTokens) ?? '0'} out</span>
                            <span className={detailsStyles.aiActivitySummaryMetricStrong}>{formatAiTokenCount(summary.totalTokens) ?? '0'} total</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {aiActivityLoading ? (
                  <div className={detailsStyles.aiActivityState}>{t('loadingAiActivity', { ns: 'conversations', defaultValue: 'Loading AI activity...' })}</div>
                ) : aiActivityError ? (
                  <div className={`${detailsStyles.aiActivityState} ${detailsStyles.aiActivityStateError}`}>{aiActivityError}</div>
                ) : aiActivityItems.length === 0 ? (
                  <div className={detailsStyles.aiActivityState}>{t('noAiActivity', { ns: 'conversations', defaultValue: 'No AI activity has been logged for this conversation yet.' })}</div>
                ) : (
                  <div className={detailsStyles.aiActivityList}>
                    {aiActivityItems.map(item => {
                      const isExpanded = aiActivityExpandedIds.has(item.id);
                      const inputValue = formatAiActivityValue(item.inputJson);
                      const outputValue = formatAiActivityValue(item.outputJson);
                      const errorValue = formatAiActivityValue(item.error);

                      const isFailed = item.status === 'Failed';

                      return (
                        <div
                          key={item.id}
                          className={[
                            detailsStyles.aiActivityItem,
                            isFailed ? detailsStyles.aiActivityItemFailed : '',
                          ].filter(Boolean).join(' ')}
                        >
                          <button
                            type="button"
                            className={detailsStyles.aiActivityItemSummary}
                            onClick={() => setAiActivityExpandedIds(current => {
                              const next = new Set(current);
                              if (next.has(item.id)) {
                                next.delete(item.id);
                              } else {
                                next.add(item.id);
                              }
                              return next;
                            })}
                          >
                            <div className={detailsStyles.aiActivityItemMeta}>
                              <span className={detailsStyles.aiActivityItemPlugin}>{formatAiActivityPluginLabel(item.pluginName)}</span>
                              <span className={detailsStyles.aiActivityItemFunction}>{formatAiActivityFunctionLabel(item.pluginName, item.functionName)}</span>
                              {item.modelName && <span className={detailsStyles.aiActivityItemModel}>{item.modelName}</span>}
                              <span
                                className={[
                                  detailsStyles.aiActivityItemStatus,
                                  isFailed ? detailsStyles.aiActivityItemStatusFailed : '',
                                ].filter(Boolean).join(' ')}
                              >
                                {formatAiActivityLabel(item.status, 'Unknown')}
                              </span>
                              <span className={detailsStyles.aiActivityItemSource}>{formatAiActivityLabel(item.triggeredBy, 'Unknown')}</span>
                              {item.runId && !aiActivityRunIdFilter ? (
                                <span
                                  className={detailsStyles.aiActivityItemRunLink}
                                  role="button"
                                  tabIndex={0}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setAiActivityRunIdFilter(item.runId ?? null);
                                  }}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      setAiActivityRunIdFilter(item.runId ?? null);
                                    }
                                  }}
                                >
                                  {formatAiRunLabel(item.runId)}
                                </span>
                              ) : null}
                              {typeof item.totalTokens === 'number' && (
                                <span className={detailsStyles.aiActivityItemTokens}>{`${formatAiTokenCount(item.totalTokens) ?? item.totalTokens} tokens`}</span>
                              )}
                              <span className={detailsStyles.aiActivityItemTime}>{formatDateTime(item.createdAt)}</span>
                            </div>
                            <span className={detailsStyles.aiActivityItemChevron}>{isExpanded ? '▾' : '▸'}</span>
                          </button>
                          {item.operationDetails && (
                            <div className={detailsStyles.aiActivityOperationDetails}>{item.operationDetails}</div>
                          )}
                          {isExpanded && (
                            <div className={detailsStyles.aiActivityItemDetails}>
                              {(typeof item.inputTokens === 'number' || typeof item.outputTokens === 'number' || typeof item.totalTokens === 'number') && (
                                <div className={detailsStyles.aiActivityTokenBreakdown}>
                                  {typeof item.inputTokens === 'number' && <span className={detailsStyles.aiActivityTokenPill}>{`${formatAiTokenCount(item.inputTokens) ?? item.inputTokens} input`}</span>}
                                  {typeof item.outputTokens === 'number' && <span className={detailsStyles.aiActivityTokenPill}>{`${formatAiTokenCount(item.outputTokens) ?? item.outputTokens} output`}</span>}
                                  {typeof item.totalTokens === 'number' && <span className={detailsStyles.aiActivityTokenPillStrong}>{`${formatAiTokenCount(item.totalTokens) ?? item.totalTokens} total`}</span>}
                                </div>
                              )}
                              <div className={detailsStyles.aiActivityPayloadGrid}>
                                {item.inputJson && (
                                  <section className={detailsStyles.aiActivityPayloadBlock}>
                                    <div className={detailsStyles.aiActivityPayloadHeader}>
                                      <span>{t('input', { ns: 'conversations', defaultValue: 'Input' })}</span>
                                      <button type="button" className={detailsStyles.noteCardSecondaryBtn} onClick={() => void copyAiActivityPayload(item.inputJson, t('input', { ns: 'conversations', defaultValue: 'Input' }))}>
                                        {t('copy', { ns: 'common' })}
                                      </button>
                                    </div>
                                    <pre className={detailsStyles.aiActivityPayloadPre}>{inputValue}</pre>
                                  </section>
                                )}
                                {item.outputJson && (
                                  <section className={detailsStyles.aiActivityPayloadBlock}>
                                    <div className={detailsStyles.aiActivityPayloadHeader}>
                                      <span>{t('output', { ns: 'conversations', defaultValue: 'Output' })}</span>
                                      <button type="button" className={detailsStyles.noteCardSecondaryBtn} onClick={() => void copyAiActivityPayload(item.outputJson, t('output', { ns: 'conversations', defaultValue: 'Output' }))}>
                                        {t('copy', { ns: 'common' })}
                                      </button>
                                    </div>
                                    <pre className={detailsStyles.aiActivityPayloadPre}>{outputValue}</pre>
                                  </section>
                                )}
                                {item.error && (
                                  <section className={`${detailsStyles.aiActivityPayloadBlock} ${detailsStyles.aiActivityPayloadBlockError}`}>
                                    <div className={detailsStyles.aiActivityPayloadHeader}>
                                      <span>{t('error', { ns: 'common', defaultValue: 'Error' })}</span>
                                      <button type="button" className={detailsStyles.noteCardSecondaryBtn} onClick={() => void copyAiActivityPayload(item.error, t('error', { ns: 'common', defaultValue: 'Error' }))}>
                                        {t('copy', { ns: 'common' })}
                                      </button>
                                    </div>
                                    <pre className={detailsStyles.aiActivityPayloadPre}>{errorValue}</pre>
                                  </section>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {aiActivityHasMore && !aiActivityLoading && !aiActivityError && (
                  <div className={detailsStyles.aiActivityFooter}>
                    <button
                      type="button"
                      className={detailsStyles.noteCardSecondaryBtn}
                      onClick={() => void loadAiActivity(selectedConv.id, true)}
                      disabled={aiActivityLoadingMore}
                    >
                      {aiActivityLoadingMore
                        ? t('loading', { ns: 'common' })
                        : t('loadMoreAiActivity', { ns: 'conversations', defaultValue: 'Load more' })}
                    </button>
                  </div>
                )}
                </div>
                </div>
              </div>
            )}
            {showAiThinkingComposerStatus ? (
              <div
                className={detailsStyles.aiThinkingComposerStatus}
                role="status"
                aria-live="polite"
                aria-busy={isAiThinkingInProgress}
              >
                <span className={`${detailsStyles.aiThinkingComposerStatusLabel} ${isAiThinkingInProgress ? detailsStyles.aiThinkingComposerStatusLabelActive : ''}`}>
                  {t('thinking', { ns: 'conversations', defaultValue: 'Thinking' })}
                  <span
                    className={`${detailsStyles.aiThinkingComposerStatusDots} ${isAiThinkingInProgress ? detailsStyles.aiThinkingComposerStatusDotsActive : ''}`}
                    aria-hidden="true"
                  >
                    <span className={detailsStyles.aiThinkingComposerStatusDot}>.</span>
                    <span className={detailsStyles.aiThinkingComposerStatusDot}>.</span>
                    <span className={detailsStyles.aiThinkingComposerStatusDot}>.</span>
                  </span>
                </span>
                {latestBroadcastThinkingMessage ? (
                  <>
                    <span className={detailsStyles.aiThinkingComposerStatusSeparator} aria-hidden="true"> · </span>
                    <span className={`${detailsStyles.aiThinkingComposerStatusDetail} ${isAiThinkingInProgress ? detailsStyles.aiThinkingComposerStatusDetailActive : ''}`}>
                      {latestBroadcastThinkingMessage}
                    </span>
                  </>
                ) : null}
              </div>
            ) : null}
            <div className={detailsStyles.composerFooter}>
              {sendingMessage && (
                <div className={detailsStyles.composerProcessingBanner} role="status" aria-live="polite">
                  <span className={detailsStyles.inlineSpinner} aria-hidden="true" />
                  <span>{sendingProgressLabel ?? t('sendingMessage', { ns: 'conversations' })}</span>
                </div>
              )}
              <div
                className={[
                  detailsStyles.composerShell,
                  dragActive ? detailsStyles.composerShellDrag : '',
                ].filter(Boolean).join(' ')}
              >
                <input
                  ref={composerFileInputRef}
                  type="file"
                  className={detailsStyles.hiddenFileInput}
                  accept={ATTACHMENT_ACCEPT}
                  multiple
                  onChange={event => onPickAttachments(event.target.files)}
                />

                {showInstagramReplyTarget && instagramReplyMode ? (
                  <InstagramComposerReplyTarget
                    context={instagramComposerContext}
                    replyMode={instagramReplyMode}
                    selectedCommentMessageId={instagramCommentTargetMessageId}
                    onReplyModeChange={setInstagramReplyMode}
                    onCommentTargetChange={setInstagramCommentTargetMessageId}
                    onClear={onClearInstagramCommentTarget}
                    onJumpToTarget={() => {
                      const targetId = instagramCommentTargetMessageId
                        ?? instagramComposerContext.pendingCommentMessageId;
                      if (targetId) {
                        void onJumpToMessage(targetId);
                      }
                    }}
                  />
                ) : null}

                {showMessengerReplyTarget && messengerReplyMode ? (
                  <MessengerComposerReplyTarget
                    context={messengerComposerContext}
                    replyMode={messengerReplyMode}
                    selectedCommentMessageId={messengerCommentTargetMessageId}
                    onReplyModeChange={setMessengerReplyMode}
                    onCommentTargetChange={setMessengerCommentTargetMessageId}
                    onClear={onClearMessengerCommentTarget}
                    onJumpToTarget={() => {
                      const targetId = messengerCommentTargetMessageId
                        ?? messengerComposerContext.pendingCommentMessageId;
                      if (targetId) {
                        void onJumpToMessage(targetId);
                      }
                    }}
                  />
                ) : null}

                {showGenericQuote && quotedMessage ? (
                  <div className={detailsStyles.composerQuotedMessage}>
                    <button
                      type="button"
                      className={detailsStyles.composerQuotedMessageJump}
                      onClick={() => onJumpToMessage(quotedMessage.id)}
                    >
                      <div className={detailsStyles.composerQuotedMessageBody}>
                        <span className={detailsStyles.composerQuotedMessageLabel}>
                          {t('replyingTo', { ns: 'conversations', name: getParticipantDisplayName(quotedMessage.sender) })}
                        </span>
                        <span className={detailsStyles.composerQuotedMessageText}>
                          {truncateMessageSnippet(getQuotedMessageSnippet(quotedMessage), 140)}
                        </span>
                      </div>
                    </button>
                    <button
                      type="button"
                      className={detailsStyles.composerQuotedMessageClear}
                      onClick={onClearQuotedMessage}
                      title={t('clearQuote', { ns: 'conversations' })}
                    >
                      {t('close', { ns: 'common' })}
                    </button>
                  </div>
                ) : null}

                <div className={detailsStyles.composerToolbar}>
                  <div className={detailsStyles.composerTools}>
                  <button
                    type="button"
                    className={detailsStyles.attachIconBtn}
                    onClick={() => composerFileInputRef.current?.click()}
                    disabled={commentRichContentBlocked}
                    title={commentRichContentBlocked
                      ? t('instagramPrivateReplyAttachmentsBlocked', { ns: 'conversations' })
                      : t('chooseFiles', { ns: 'conversations' })}
                    aria-label={t('chooseFiles', { ns: 'conversations' })}
                  >
                    <svg className={detailsStyles.attachIconSvg} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className={`${detailsStyles.audioRecordBtn} ${isRecordingAudio ? detailsStyles.audioRecordBtnActive : ''}`}
                    onClick={() => void onAudioRecordToggle()}
                    aria-pressed={isRecordingAudio}
                    disabled={commentRichContentBlocked}
                    title={commentRichContentBlocked
                      ? t('instagramPrivateReplyAttachmentsBlocked', { ns: 'conversations' })
                      : isRecordingAudio
                        ? t('stopRecordingAudio', { ns: 'conversations' })
                        : t('startRecordingAudio', { ns: 'conversations' })}
                  >
                    <svg className={detailsStyles.audioIconSvg} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M12 15a3 3 0 0 0 3-3V7a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3Zm5-3a1 1 0 1 1 2 0 7 7 0 0 1-6 6.93V21h3a1 1 0 1 1 0 2H8a1 1 0 1 1 0-2h3v-2.07A7 7 0 0 1 5 12a1 1 0 1 1 2 0 5 5 0 0 0 10 0Z" />
                    </svg>
                    {isRecordingAudio && <span className={detailsStyles.recordDot} aria-hidden="true" />}
                  </button>
                  <ConversationShortcutMenu
                    isOpen={isShortcutMenuOpen}
                    onToggle={() => {
                      setIsEmojiPickerOpen(false);
                      setIsShortcutMenuOpen(current => !current);
                    }}
                    onClose={() => setIsShortcutMenuOpen(false)}
                    onSelectCommand={onShortcutSelectCommand}
                    buttonClassName={detailsStyles.shortcutBtn}
                    iconClassName={detailsStyles.shortcutIconSvg}
                  />
                  <ComposerEmojiPicker
                    isOpen={isEmojiPickerOpen}
                    onToggle={() => {
                      setIsShortcutMenuOpen(false);
                      setIsEmojiPickerOpen(current => !current);
                    }}
                    onClose={() => setIsEmojiPickerOpen(false)}
                    onPick={insertComposerEmoji}
                    buttonClassName={`${detailsStyles.shortcutBtn} ${isEmojiPickerOpen ? detailsStyles.emojiPickerBtnActive : ''}`}
                    iconClassName={detailsStyles.attachIconSvg}
                    disabled={isInteractiveComposerOpen}
                    title={t('openEmojiPicker', { ns: 'conversations' })}
                  />
                  {!isWhatsAppAppLine ? (
                    <button
                      type="button"
                      className={`${detailsStyles.interactiveToggleBtn} ${isInteractiveComposerOpen ? detailsStyles.interactiveToggleBtnActive : ''} ${!isInteractiveComposerOpen && hasInteractiveDraft ? detailsStyles.interactiveToggleBtnDraft : ''}`}
                      onClick={() => setIsInteractiveComposerOpen(current => !current)}
                      disabled={commentRichContentBlocked}
                      title={commentRichContentBlocked
                        ? t('instagramPrivateReplyAttachmentsBlocked', { ns: 'conversations' })
                        : undefined}
                    >
                      {isInteractiveComposerOpen
                        ? t('interactiveModeActiveButton', { ns: 'conversations' })
                        : hasInteractiveDraft
                          ? t('interactiveDraftButton', { ns: 'conversations' })
                          : t('interactiveComposerButton', { ns: 'conversations' })}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={detailsStyles.aiDraftBtn}
                    onClick={onGenerateAiSummary}
                    disabled={!meSender || isAiBusy || isInteractiveComposerOpen}
                  >
                    {aiSummaryLoading ? t('thinking', { ns: 'conversations' }) : t('summarizeConversation', { ns: 'conversations' })}
                  </button>
                  <button
                    type="button"
                    className={detailsStyles.aiDraftBtn}
                    onClick={onGenerateAiDraft}
                    disabled={!meSender || isAiBusy || isInteractiveComposerOpen}
                  >
                    {aiDraftLoading ? t('thinking', { ns: 'conversations' }) : t('askAi', { ns: 'conversations' })}
                  </button>
                  {isRecordingAudio && (
                    <span className={detailsStyles.dropHint}>
                      {t('recordingAudioHint', { ns: 'conversations' })}
                    </span>
                  )}
                  </div>
                  <div className={detailsStyles.composerUtilityDivider} aria-hidden="true" />
                  <div className={detailsStyles.composerUtilityGroup}>
                    <button
                      type="button"
                      className={detailsStyles.aiDraftBtn}
                      onClick={() => onOpenConversationNotes(selectedConv.id)}
                    >
                      {t('notes', { ns: 'common', defaultValue: 'Notes' })}
                      {visibleNoteCount > 0 ? (
                        <span className={detailsStyles.composerUtilityBadge}>{visibleNoteCount}</span>
                      ) : null}
                    </button>
                    {canReadTasks ? (
                      <button
                        type="button"
                        className={`${detailsStyles.aiDraftBtn} ${isLinkedTasksExpanded ? detailsStyles.composerUtilityBtnActive : ''}`}
                        onClick={() => setIsLinkedTasksExpanded(current => !current)}
                        aria-expanded={isLinkedTasksExpanded}
                        aria-label={isLinkedTasksExpanded
                          ? t('hideLinkedTasks', { ns: 'conversations' })
                          : t('linkedTasksTitle', { ns: 'conversations' })}
                      >
                        {renderLinkedTasksIcon(detailsStyles.composerUtilityBtnIcon)}
                        {isLinkedTasksExpanded
                          ? t('hideLinkedTasksShort', { ns: 'conversations' })
                          : t('linkedTasksTitle', { ns: 'conversations' })}
                        {linkedTaskCount > 0 ? (
                          <span className={detailsStyles.composerUtilityBadge}>{linkedTaskCount}</span>
                        ) : null}
                      </button>
                    ) : null}
                  </div>
                </div>

                {canReadTasks && isLinkedTasksExpanded && (
                  <div className={detailsStyles.composerTasksExpanded}>
                    <div className={detailsStyles.utilityPanelHeader}>
                      <div className={detailsStyles.utilityPanelTitle}>
                        {renderLinkedTasksIcon(detailsStyles.utilityPanelTitleIcon)}
                        {t('linkedTasksTitle', { ns: 'conversations' })}
                        {linkedTaskCount > 0 ? (
                          <span className={detailsStyles.composerUtilityBadge}>{linkedTaskCount}</span>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        className={detailsStyles.utilityPanelCloseBtn}
                        onClick={() => {
                          setIsLinkedTasksExpanded(false);
                          setIsTaskPickerOpen(false);
                        }}
                        aria-label={t('closeLinkedTasksPanel', { ns: 'conversations' })}
                      >
                        {renderPanelCloseIcon(detailsStyles.utilityPanelCloseIcon)}
                        <span>{t('close', { ns: 'common' })}</span>
                      </button>
                    </div>
                    <div className={detailsStyles.utilityPanelBody}>
                    <div className={detailsStyles.taskCardStack}>
                      {linkedTasksLoading ? (
                        <div className={detailsStyles.taskCardState}>{t('loadingLinkedTasks', { ns: 'conversations' })}</div>
                      ) : linkedTasksError ? (
                        <div className={`${detailsStyles.taskCardState} ${detailsStyles.taskCardStateError}`}>{linkedTasksError}</div>
                      ) : linkedTasks.length === 0 ? (
                        <div className={`${detailsStyles.noteCardBody} ${detailsStyles.noteCardBodyEmpty}`}>
                          {t('noLinkedTasks', { ns: 'conversations' })}
                        </div>
                      ) : (
                        <div className={detailsStyles.taskLinkList}>
                          {linkedTasks.map(task => {
                            const link = getConversationTaskLink(task, selectedConv.id);
                            const isPromoting = promotingTaskId === task.id;
                            const isUnlinking = unlinkingTaskId === task.id;

                            return (
                              <div key={task.id} className={detailsStyles.taskLinkRow}>
                                <div className={detailsStyles.taskLinkCopy}>
                                  <div className={detailsStyles.taskLinkHead}>
                                    <span className={detailsStyles.taskLinkKey}>{task.taskKey}</span>
                                    {link?.isPrimary ? (
                                      <span className={detailsStyles.taskLinkBadge}>{t('primaryTaskLink', { ns: 'conversations' })}</span>
                                    ) : null}
                                  </div>
                                  <div className={detailsStyles.taskLinkTitle}>{task.title}</div>
                                  <div className={detailsStyles.taskLinkMeta}>
                                    <span>{task.status}</span>
                                    {task.dueDate ? <span>{formatLongDateLabel(task.dueDate)}</span> : null}
                                    {link?.linkedAt ? <span>{formatDateTime(link.linkedAt)}</span> : null}
                                  </div>
                                </div>
                                {canManageTasks ? (
                                  <div className={detailsStyles.taskLinkActions}>
                                    <button
                                      type="button"
                                      className={detailsStyles.noteCardSecondaryBtn}
                                      onClick={() => handleOpenTask(task.id)}
                                      disabled={isPromoting || isUnlinking}
                                    >
                                      {t('openTask', { ns: 'conversations' })}
                                    </button>
                                    {!link?.isPrimary ? (
                                      <button
                                        type="button"
                                        className={detailsStyles.noteCardSecondaryBtn}
                                        onClick={() => void handleMakeTaskPrimary(task.id)}
                                        disabled={isPromoting || isUnlinking}
                                      >
                                        {isPromoting ? t('makingTaskPrimary', { ns: 'conversations' }) : t('makePrimaryTaskAction', { ns: 'conversations' })}
                                      </button>
                                    ) : null}
                                    <button
                                      type="button"
                                      className={`${detailsStyles.noteCardSecondaryBtn} ${detailsStyles.taskDangerBtn}`}
                                      onClick={() => void handleUnlinkTask(task.id)}
                                      disabled={isPromoting || isUnlinking}
                                    >
                                      {isUnlinking ? t('unlinkingTask', { ns: 'conversations' }) : t('unlinkTaskAction', { ns: 'conversations' })}
                                    </button>
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {canManageTasks && (
                        <div className={detailsStyles.noteCardActions}>
                          <button
                            type="button"
                            className={detailsStyles.noteCardSecondaryBtn}
                            onClick={() => openCreateTaskModal()}
                          >
                            {t('newTask', { ns: 'conversations' })}
                          </button>
                          <button
                            type="button"
                            className={detailsStyles.noteCardEditBtn}
                            onClick={() => setIsTaskPickerOpen(current => !current)}
                          >
                            {isTaskPickerOpen
                              ? t('cancelLinkTask', { ns: 'conversations' })
                              : t('linkExistingTask', { ns: 'conversations' })}
                          </button>
                        </div>
                      )}

                      {canManageTasks && isTaskPickerOpen && (
                        <div className={detailsStyles.taskPickerPanel}>
                          <input
                            type="search"
                            value={taskSearchTerm}
                            onChange={event => setTaskSearchTerm(event.target.value)}
                            className={`text-input ${detailsStyles.taskSearchInput}`}
                            placeholder={t('searchTasksToLink', { ns: 'conversations' })}
                            aria-label={t('searchTasksToLink', { ns: 'conversations' })}
                          />
                          {linkedTasks.length > 0 && (
                            <label className={detailsStyles.taskPrimaryToggle}>
                              <input
                                type="checkbox"
                                checked={linkTaskAsPrimary}
                                onChange={event => setLinkTaskAsPrimary(event.target.checked)}
                              />
                              <span>{t('makeTaskPrimary', { ns: 'conversations' })}</span>
                            </label>
                          )}
                          {taskSearchError ? (
                            <div className={`${detailsStyles.taskCardState} ${detailsStyles.taskCardStateError}`}>{taskSearchError}</div>
                          ) : null}
                          {taskSearchLoading ? (
                            <div className={detailsStyles.taskCardState}>{t('searchingTasks', { ns: 'conversations' })}</div>
                          ) : taskSearchResults.length === 0 ? (
                            <div className={detailsStyles.taskCardState}>{t('noTasksFoundForLink', { ns: 'conversations' })}</div>
                          ) : (
                            <div className={detailsStyles.taskSearchResults}>
                              {taskSearchResults.map(task => {
                                const isAlreadyLinked = Boolean(getConversationTaskLink(task, selectedConv.id));
                                const isBusy = linkingTaskId === task.id;

                                return (
                                  <div key={task.id} className={detailsStyles.taskSearchRow}>
                                    <div className={detailsStyles.taskLinkCopy}>
                                      <div className={detailsStyles.taskLinkHead}>
                                        <span className={detailsStyles.taskLinkKey}>{task.taskKey}</span>
                                        {isAlreadyLinked ? (
                                          <span className={detailsStyles.taskLinkBadge}>{t('alreadyLinkedTask', { ns: 'conversations' })}</span>
                                        ) : null}
                                      </div>
                                      <div className={detailsStyles.taskLinkTitle}>{task.title}</div>
                                      <div className={detailsStyles.taskLinkMeta}>
                                        <span>{task.status}</span>
                                        {task.dueDate ? <span>{formatLongDateLabel(task.dueDate)}</span> : null}
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      className={detailsStyles.noteCardSecondaryBtn}
                                      onClick={() => void handleLinkTask(task.id)}
                                      disabled={isAlreadyLinked || isBusy}
                                    >
                                      {isBusy ? t('linkingTask', { ns: 'conversations' }) : t('linkTaskAction', { ns: 'conversations' })}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    </div>
                  </div>
                )}

                {(attachments.length > 0 || Boolean(sendError)) && (
                  <div
                    id="conversations-uploader-panel"
                    className={`${detailsStyles.uploaderPanel} ${detailsStyles.uploaderPanelExpanded}`}
                  >
                    {attachments.length > 0 && (
                      <div className={detailsStyles.attachmentList}>
                        {attachments.map((attachment, index) => (
                          <div key={attachment.id} className={detailsStyles.attachmentCard}>
                            <div className={detailsStyles.attachmentPreview}>
                              {attachment.kind === 'image' && attachment.previewUrl && (
                                <img className={detailsStyles.attachmentImagePreview} src={attachment.previewUrl} alt={attachment.file.name} />
                              )}
                              {attachment.kind === 'audio' && attachment.previewUrl && (
                                <audio className={detailsStyles.mediaPlayer} controls preload="metadata" src={attachment.previewUrl} />
                              )}
                              {attachment.kind === 'video' && attachment.previewUrl && (
                                <video className={detailsStyles.mediaPlayer} controls preload="metadata" src={attachment.previewUrl} />
                              )}
                              {attachment.kind === 'document' && (
                                <div className={detailsStyles.attachmentIcon} title={t('documentAttachment', { ns: 'conversations' })}>
                                  {renderDocumentIcon(detailsStyles.attachmentIconSvg)}
                                </div>
                              )}
                            </div>
                            <div className={detailsStyles.attachmentMeta}>
                              <div className={detailsStyles.attachmentName}>{attachment.file.name}</div>
                              <div className={detailsStyles.attachmentInfo}>
                                <span
                                  className={`${detailsStyles.attachmentTypeBadge} ${
                                    attachment.kind === 'image'
                                      ? detailsStyles.attachmentTypeImage
                                      : attachment.kind === 'audio'
                                        ? detailsStyles.attachmentTypeAudio
                                        : attachment.kind === 'video'
                                          ? detailsStyles.attachmentTypeVideo
                                          : detailsStyles.attachmentTypeDocument
                                  }`}
                                  aria-hidden="true"
                                >
                                  {renderAttachmentTypeIcon(attachment.kind)}
                                </span>
                                {attachment.kind === 'image'
                                  ? t('imageAttachment', { ns: 'conversations' })
                                  : attachment.kind === 'audio'
                                    ? t('audioAttachment', { ns: 'conversations' })
                                    : attachment.kind === 'video'
                                      ? t('videoAttachment', { ns: 'conversations' })
                                      : t('documentAttachment', { ns: 'conversations' })} • {formatBytes(attachment.file.size)}{index === 0 && composer.trim() ? ` • ${t('includesCaption', { ns: 'conversations' })}` : ''}
                              </div>
                            </div>
                            <button
                              type="button"
                              className={detailsStyles.clearAttachmentBtn}
                              onClick={() => removeAttachment(attachment.id)}
                            >
                              {t('removeAttachment', { ns: 'conversations' })}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {sendError && <div className={detailsStyles.sendError}>{sendError}</div>}
                  </div>
                )}

                <div className={detailsStyles.composerDivider} aria-hidden="true" />

                {showWhatsAppLinkPreviewToggle ? (
                  <label className={detailsStyles.composerWhatsAppPreviewToggle}>
                    <input
                      type="checkbox"
                      checked={whatsAppLinkPreviewEnabled}
                      onChange={event => setWhatsAppLinkPreviewEnabled(event.target.checked)}
                    />
                    <span>{t('whatsAppLinkPreview', { ns: 'conversations' })}</span>
                  </label>
                ) : null}

                <div className={detailsStyles.composer}>
                  <textarea
                    ref={composerInputRef}
                    className={detailsStyles.input}
                    placeholder={t(composerPlaceholderKey, { ns: 'conversations' })}
                    rows={3}
                    value={composer}
                    onFocus={onComposerFocus}
                    onChange={event => onComposerChange(event.target.value, event.target.selectionStart ?? event.target.value.length)}
                    onClick={event => onComposerSelectionChange((event.currentTarget as HTMLTextAreaElement).selectionStart ?? composer.length)}
                    onKeyUp={event => onComposerSelectionChange((event.currentTarget as HTMLTextAreaElement).selectionStart ?? composer.length)}
                    onKeyDown={event => {
                      if (onComposerKeyDown(event)) {
                        return;
                      }

                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        void onSend();
                      }
                    }}
                  />
                  {mentionMenuOpen && mentionSuggestions.length > 0 && (
                    <div className={detailsStyles.mentionMenu} role="listbox" aria-label={t('participants', { ns: 'conversations' })}>
                      {mentionSuggestions.map((option, index) => {
                        const isActive = mentionActiveIndex === index;
                        return (
                          <button
                            key={`${option.type}:${option.id}`}
                            type="button"
                            role="option"
                            aria-selected={isActive}
                            className={`${detailsStyles.mentionMenuItem} ${isActive ? detailsStyles.mentionMenuItemActive : ''}`}
                            onMouseDown={event => {
                              event.preventDefault();
                              onSelectMention(option);
                            }}
                          >
                            <ParticipantAvatar participantType={option.type} className={detailsStyles.mentionMenuAvatar} />
                            <span className={detailsStyles.mentionMenuText}>
                              <span className={detailsStyles.mentionMenuName}>{option.name}</span>
                              {option.email ? <span className={detailsStyles.mentionMenuMeta}>{option.email}</span> : <span className={detailsStyles.mentionMenuMeta}>{option.type}</span>}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {commandMenuOpen && commandSuggestions.length > 0 && (
                    <div className={detailsStyles.mentionMenu} role="listbox" aria-label={t('tabCommands', { ns: 'tools' })}>
                      {commandSuggestions.map((command, index) => {
                        const isActive = commandActiveIndex === index;
                        return (
                          <button
                            key={command.commandId}
                            type="button"
                            role="option"
                            aria-selected={isActive}
                            className={`${detailsStyles.mentionMenuItem} ${isActive ? detailsStyles.mentionMenuItemActive : ''}`}
                            onMouseDown={event => {
                              event.preventDefault();
                              onSelectCommand?.(command);
                            }}
                          >
                            <span className={detailsStyles.mentionMenuText}>
                              <span className={detailsStyles.mentionMenuName}>/{command.trigger}</span>
                              <span className={detailsStyles.mentionMenuMeta}>
                                {command.displayName}
                                {command.description ? ` — ${command.description}` : ''}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div className={detailsStyles.sendSplit} ref={scheduleMenuRef}>
                    <button
                      className={detailsStyles.sendSplitPrimary}
                      onClick={() => void onSend()}
                      disabled={!canSend}
                      aria-busy={sendingMessage}
                      aria-label={sendingMessage
                        ? (sendingProgressLabel ?? t('sendingMessage', { ns: 'conversations' }))
                        : t('sendMessage', { ns: 'conversations' })}
                    >
                      {sendingMessage ? (
                        <span className={detailsStyles.inlineSpinner} aria-hidden="true" />
                      ) : (
                        <svg className={detailsStyles.sendIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M4 12h13M12 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </button>
                    <button
                      type="button"
                      className={detailsStyles.sendSplitMenu}
                      onClick={() => setIsScheduleMenuOpen(open => !open)}
                      disabled={scheduleDisabled}
                      aria-label={t('scheduleMessage', { ns: 'conversations' })}
                    >
                      ▾
                    </button>

                    {isScheduleMenuOpen && (
                      <div className={detailsStyles.scheduleMenu}>
                        <div className={detailsStyles.scheduleMenuTitle}>{t('scheduleMessage', { ns: 'conversations' })}</div>
                        <button
                          type="button"
                          className={detailsStyles.scheduleMenuItem}
                          onClick={() => void handleScheduleAt(buildLaterTodayPreset())}
                        >
                          {t('laterTodayPreset', { ns: 'conversations' })}
                        </button>
                        <button
                          type="button"
                          className={detailsStyles.scheduleMenuItem}
                          onClick={() => void handleScheduleAt(buildMondayMorningPreset())}
                        >
                          {t('mondayMorningPreset', { ns: 'conversations' })}
                        </button>
                        <div className={detailsStyles.scheduleMenuDivider} />
                        <button
                          type="button"
                          className={detailsStyles.scheduleMenuItem}
                          onClick={() => {
                            setShowCustomScheduleInput(true);
                            if (!customScheduleValue) {
                              const seed = scheduledAt ?? new Date(Date.now() + 30 * 60 * 1000);
                              setCustomScheduleValue(formatDateTimeInputValue(seed));
                            }
                          }}
                        >
                          {t('customTime', { ns: 'conversations' })}
                        </button>

                        {showCustomScheduleInput && (
                          <div className={detailsStyles.scheduleCustomRow}>
                            <input
                              type="datetime-local"
                              className={detailsStyles.scheduledInput}
                              value={customScheduleValue}
                              onChange={event => setCustomScheduleValue(event.target.value)}
                            />
                            <button
                              type="button"
                              className={detailsStyles.scheduleApplyBtn}
                              onClick={() => {
                                if (!customScheduleValue) {
                                  return;
                                }
                                const parsedScheduleTime = parseDateTimeInputValue(customScheduleValue);
                                if (!parsedScheduleTime) {
                                  return;
                                }
                                void handleScheduleAt(parsedScheduleTime);
                              }}
                            >
                              {t('schedule', { ns: 'conversations' })}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {isInteractiveComposerOpen && !isWhatsAppAppLine && (
                  <div
                    className={detailsStyles.modalOverlay}
                    role="dialog"
                    aria-modal="true"
                    aria-label={t('interactiveComposerTitle', { ns: 'conversations' })}
                  >
                    <div className={`${detailsStyles.modalCard} ${detailsStyles.interactiveModalCard}`}>
                      <div className={detailsStyles.modalHeader}>
                        <div>
                            <div className={detailsStyles.modalTitle}>{t('interactiveComposerTitle', { ns: 'conversations' })}</div>
                            <div className={detailsStyles.interactiveComposerHint}>{t('interactiveComposerHint', { ns: 'conversations' })}</div>
                        </div>
                        <button
                          type="button"
                          className={detailsStyles.modalCloseBtn}
                          onClick={() => setIsInteractiveComposerOpen(false)}
                        >
                          {t('close', { ns: 'common' })}
                        </button>
                      </div>

                      <div className={detailsStyles.interactiveComposerCard}>
                        <div className={detailsStyles.interactiveModeTabs}>
                          <button
                            type="button"
                            className={`${detailsStyles.interactiveModeTab} ${interactiveComposerMode === 'list' ? detailsStyles.interactiveModeTabActive : ''}`}
                            onClick={() => setInteractiveComposerMode('list')}
                          >
                            {t('interactiveComposerModeList', { ns: 'conversations' })}
                          </button>
                          <button
                            type="button"
                            className={`${detailsStyles.interactiveModeTab} ${interactiveComposerMode === 'buttons' ? detailsStyles.interactiveModeTabActive : ''}`}
                            onClick={() => setInteractiveComposerMode('buttons')}
                          >
                            {t('interactiveComposerModeButtons', { ns: 'conversations' })}
                          </button>
                        </div>
                        <div className={detailsStyles.interactiveComposerStats}>
                          {interactiveComposerMode === 'list' ? (
                            <>
                              <span className={detailsStyles.interactiveComposerStat}>{t('interactiveSectionsValue', { ns: 'conversations', count: interactiveSectionCount })}</span>
                              <span className={detailsStyles.interactiveComposerStat}>{t('interactiveOptionsValue', { ns: 'conversations', count: interactiveOptionCount })}</span>
                            </>
                          ) : (
                            <span className={detailsStyles.interactiveComposerStat}>{t('interactiveButtonsValue', { ns: 'conversations', count: interactiveButtonCount })}</span>
                          )}
                        </div>

                        <div className={detailsStyles.interactiveComposerScrollArea}>
                          <div className={detailsStyles.interactiveFieldGrid}>
                            {interactiveComposerMode === 'list' ? (
                              <>
                                <label className={detailsStyles.interactiveField}>
                                  <span>{t('interactiveButton', { ns: 'conversations' })}</span>
                                  <input
                                    className={detailsStyles.interactiveInput}
                                    value={interactiveListDraft.buttonText}
                                    onChange={event => updateInteractiveList(current => ({ ...current, buttonText: event.target.value }))}
                                    placeholder={t('interactiveButtonPlaceholder', { ns: 'conversations' })}
                                  />
                                </label>
                                <label className={`${detailsStyles.interactiveField} ${detailsStyles.interactiveFieldFull}`}>
                                  <span>{t('interactiveBody', { ns: 'conversations' })}</span>
                                  <textarea
                                    className={detailsStyles.interactiveTextarea}
                                    rows={3}
                                    value={interactiveListDraft.bodyText}
                                    onChange={event => updateInteractiveList(current => ({ ...current, bodyText: event.target.value }))}
                                    placeholder={t('interactiveBodyPlaceholder', { ns: 'conversations' })}
                                  />
                                </label>
                              </>
                            ) : (
                              <label className={`${detailsStyles.interactiveField} ${detailsStyles.interactiveFieldFull}`}>
                                <span>{t('interactiveBody', { ns: 'conversations' })}</span>
                                <textarea
                                  className={detailsStyles.interactiveTextarea}
                                  rows={3}
                                  value={interactiveButtonsDraft.bodyText}
                                  onChange={event => updateInteractiveButtons(current => ({ ...current, bodyText: event.target.value }))}
                                  placeholder={t('interactiveButtonsBodyPlaceholder', { ns: 'conversations' })}
                                />
                              </label>
                            )}
                          </div>

                          <details className={detailsStyles.interactiveAdvancedPanel}>
                            <summary className={detailsStyles.interactiveAdvancedSummary}>{t('interactiveOptionalSettings', { ns: 'conversations' })}</summary>
                            <div className={detailsStyles.interactiveFieldGrid}>
                              <label className={detailsStyles.interactiveField}>
                                <span>{t('interactiveHeader', { ns: 'conversations' })}</span>
                                <input
                                  className={detailsStyles.interactiveInput}
                                  value={interactiveComposerMode === 'list' ? (interactiveListDraft.headerText || '') : (interactiveButtonsDraft.headerText || '')}
                                  onChange={event => interactiveComposerMode === 'list'
                                    ? updateInteractiveList(current => ({ ...current, headerText: event.target.value }))
                                    : updateInteractiveButtons(current => ({ ...current, headerText: event.target.value }))}
                                  placeholder={t('interactiveHeaderPlaceholder', { ns: 'conversations' })}
                                />
                              </label>
                              <label className={detailsStyles.interactiveField}>
                                <span>{t('interactiveFooter', { ns: 'conversations' })}</span>
                                <input
                                  className={detailsStyles.interactiveInput}
                                  value={interactiveComposerMode === 'list' ? (interactiveListDraft.footerText || '') : (interactiveButtonsDraft.footerText || '')}
                                  onChange={event => interactiveComposerMode === 'list'
                                    ? updateInteractiveList(current => ({ ...current, footerText: event.target.value }))
                                    : updateInteractiveButtons(current => ({ ...current, footerText: event.target.value }))}
                                  placeholder={t('interactiveFooterPlaceholder', { ns: 'conversations' })}
                                />
                              </label>
                            </div>
                          </details>

                          {interactiveComposerMode === 'list' ? (
                            <div className={detailsStyles.interactiveSectionsList}>
                              {interactiveListDraft.sections.map((section, sectionIndex) => (
                                <div key={`interactive-section-${sectionIndex}`} className={detailsStyles.interactiveSectionCard}>
                                  <div className={detailsStyles.interactiveSectionHeader}>
                                    <div className={detailsStyles.interactiveSectionMeta}>
                                      <strong>{section.title?.trim() || t('interactiveSection', { ns: 'conversations', index: sectionIndex + 1 })}</strong>
                                      <span>{t('interactiveOptionsValue', { ns: 'conversations', count: section.rows.length })}</span>
                                    </div>
                                    <div className={detailsStyles.interactiveSectionControls}>
                                      <button
                                        type="button"
                                        className={detailsStyles.interactiveSectionToggle}
                                        onClick={() => toggleInteractiveSection(sectionIndex)}
                                      >
                                        {collapsedInteractiveSections.has(sectionIndex)
                                          ? t('expand', { ns: 'conversations' })
                                          : t('minimize', { ns: 'conversations' })}
                                      </button>
                                      {interactiveListDraft.sections.length > 1 && (
                                        <button
                                          type="button"
                                          className={detailsStyles.interactiveGhostBtn}
                                          onClick={() => removeInteractiveSection(sectionIndex)}
                                        >
                                          {t('removeSection', { ns: 'conversations' })}
                                        </button>
                                      )}
                                    </div>
                                  </div>

                                  {!collapsedInteractiveSections.has(sectionIndex) && (
                                    <div className={detailsStyles.interactiveSectionBody}>
                                      <label className={`${detailsStyles.interactiveField} ${detailsStyles.interactiveFieldFull}`}>
                                        <span>{t('interactiveSection', { ns: 'conversations', index: sectionIndex + 1 })}</span>
                                        <input
                                          className={detailsStyles.interactiveInput}
                                          value={section.title || ''}
                                          onChange={event => updateInteractiveList(current => ({
                                            ...current,
                                            sections: current.sections.map((candidateSection, candidateIndex) => candidateIndex === sectionIndex
                                              ? { ...candidateSection, title: event.target.value }
                                              : candidateSection),
                                          }))}
                                          placeholder={t('interactiveSectionPlaceholder', { ns: 'conversations' })}
                                        />
                                      </label>

                                      <div className={detailsStyles.interactiveRowsList}>
                                        {section.rows.map((row, rowIndex) => (
                                          <div key={`interactive-row-${sectionIndex}-${rowIndex}`} className={detailsStyles.interactiveRowCard}>
                                            <div className={detailsStyles.interactiveRowGrid}>
                                              <label className={detailsStyles.interactiveField}>
                                                <span>{t('interactiveRowTitle', { ns: 'conversations' })}</span>
                                                <input
                                                  className={detailsStyles.interactiveInput}
                                                  value={row.title}
                                                  onChange={event => updateInteractiveList(current => ({
                                                    ...current,
                                                    sections: current.sections.map((candidateSection, candidateIndex) => candidateIndex === sectionIndex
                                                      ? {
                                                          ...candidateSection,
                                                          rows: candidateSection.rows.map((candidateRow, candidateRowIndex) => candidateRowIndex === rowIndex
                                                            ? { ...candidateRow, title: event.target.value }
                                                            : candidateRow),
                                                        }
                                                      : candidateSection),
                                                  }))}
                                                  placeholder={t('interactiveRowTitlePlaceholder', { ns: 'conversations' })}
                                                />
                                              </label>
                                            </div>
                                            <label className={`${detailsStyles.interactiveField} ${detailsStyles.interactiveFieldFull}`}>
                                              <span>{t('interactiveRowDescription', { ns: 'conversations' })}</span>
                                              <input
                                                className={detailsStyles.interactiveInput}
                                                value={row.description || ''}
                                                onChange={event => updateInteractiveList(current => ({
                                                  ...current,
                                                  sections: current.sections.map((candidateSection, candidateIndex) => candidateIndex === sectionIndex
                                                    ? {
                                                        ...candidateSection,
                                                        rows: candidateSection.rows.map((candidateRow, candidateRowIndex) => candidateRowIndex === rowIndex
                                                          ? { ...candidateRow, description: event.target.value }
                                                          : candidateRow),
                                                      }
                                                    : candidateSection),
                                                }))}
                                                placeholder={t('interactiveRowDescriptionPlaceholder', { ns: 'conversations' })}
                                              />
                                            </label>
                                            {section.rows.length > 1 && (
                                              <button
                                                type="button"
                                                className={detailsStyles.interactiveGhostBtn}
                                                onClick={() => removeInteractiveRow(sectionIndex, rowIndex)}
                                              >
                                                {t('removeRow', { ns: 'conversations' })}
                                              </button>
                                            )}
                                          </div>
                                        ))}
                                      </div>

                                      <button
                                        type="button"
                                        className={detailsStyles.interactiveSecondaryBtn}
                                        onClick={() => addInteractiveRow(sectionIndex)}
                                      >
                                        {t('addRow', { ns: 'conversations' })}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className={detailsStyles.interactiveSectionsList}>
                              {interactiveButtonsDraft.buttons.map((button, buttonIndex) => (
                                <div key={`interactive-button-${buttonIndex}`} className={detailsStyles.interactiveRowCard}>
                                  <div className={detailsStyles.interactiveRowGrid}>
                                    <label className={detailsStyles.interactiveField}>
                                      <span>{t('interactiveReplyButtonTitle', { ns: 'conversations' })}</span>
                                      <input
                                        className={detailsStyles.interactiveInput}
                                        value={button.title}
                                        onChange={event => updateInteractiveButtons(current => ({
                                          ...current,
                                          buttons: current.buttons.map((candidateButton, candidateIndex) => candidateIndex === buttonIndex
                                            ? { ...candidateButton, title: event.target.value }
                                            : candidateButton),
                                        }))}
                                        placeholder={t('interactiveReplyButtonTitlePlaceholder', { ns: 'conversations' })}
                                      />
                                    </label>
                                  </div>
                                  {interactiveButtonsDraft.buttons.length > 1 && (
                                    <button
                                      type="button"
                                      className={detailsStyles.interactiveGhostBtn}
                                      onClick={() => removeInteractiveButton(buttonIndex)}
                                    >
                                      {t('removeInteractiveReplyButton', { ns: 'conversations' })}
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {sendError && <div className={detailsStyles.modalError}>{sendError}</div>}

                        <div className={detailsStyles.interactiveComposerFooter}>
                          <button
                            type="button"
                            className={detailsStyles.interactiveSecondaryBtn}
                            onClick={interactiveComposerMode === 'list' ? addInteractiveSection : addInteractiveButton}
                            disabled={interactiveComposerMode === 'buttons' && isInteractiveButtonAddAtLimit}
                          >
                            {interactiveComposerMode === 'list'
                              ? t('addSection', { ns: 'conversations' })
                              : t('addInteractiveReplyButton', { ns: 'conversations' })}
                          </button>
                          <button
                            type="button"
                            className={detailsStyles.modalPrimaryBtn}
                            onClick={() => void onSend()}
                            disabled={!canSend}
                          >
                            {t('sendMessage', { ns: 'conversations' })}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {isScheduledPanelExpanded && (
                  <ScheduledMessagesPanel
                    items={scheduledMessages}
                    error={scheduleError}
                    onCancel={onCancelScheduledMessage}
                    onReschedule={onUpdateScheduledMessageTime}
                    onJumpToQuotedMessage={onJumpToMessage}
                  />
                )}
              </div>
            </div>
          </div>
          {dragActive ? (
            <div className={detailsStyles.conversationDropOverlay} role="status" aria-live="polite">
              <div className={detailsStyles.conversationDropOverlayCard}>
                <svg className={detailsStyles.conversationDropOverlayIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                  <path d="M12 16V5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  <path d="M8.5 8.5 12 5l3.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M5 15.5V18a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
                <div className={detailsStyles.conversationDropOverlayTitle}>
                  {t('dropFilesOverlayTitle', { ns: 'conversations' })}
                </div>
                <div className={detailsStyles.conversationDropOverlayHint}>
                  {t('dropFilesOverlayHint', { ns: 'conversations' })}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
      {mentionDetailsPopup}
      {imageViewerPopup}
    </div>
  );
};

export default ConversationsDetailsPane;
